//latest
import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";
import { Modal, Button, Form } from "react-bootstrap";
import toast, { Toaster } from "react-hot-toast";
import Select from "react-select";
import { getUserData } from "./const";
import "../assets/css/theme-table-header.css";
import {
  PURCHASE_RATE_CUT_LINE_ERROR_OPTIONS,
  collectLineItemFieldErrors,
  lineCellErrorStyle,
  pruneLineFieldErrors,
} from "../utils/lineItemFieldErrors";
import { useIndiaStateCity } from "../utils/useIndiaStateCity";
import {
  listBankAccounts,
  showProductGroup,
  showMetalGroup,
  listSuppliers,
  settleRateCut,
  getRateCutSummary,
  listCustomerUsers,
  listProducts,
  showProducts,
  showOrderQueue,
  createCustomerUser,
  isErpApiSuccess,
} from "../erpClient";
import {
  createPurchasesIdempotencyKey,
  isPurchasesApiSuccess,
  mapPurchasePaymentsForUpdateApi,
} from "../utils/purchasesErpAdapter";
import { mapCustomerUsersToPartyList } from "../utils/customerUsersErpAdapter";
import {
  normalizeProductStockListResponse,
  normalizeProductStockShowResponse,
} from "../utils/productStockErpAdapter";
import {
  searchProductGroupSelectOptions,
  mergeProductGroupOptions,
} from "../utils/productGroupOptions";

import { useLocation, useNavigate } from "react-router-dom";
import useFillSalesFromCustomOrder from "../utils/useFillSalesFromCustomOrder";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import PaymentSection from "../components/PaymentSection";

import { sanitizeMobileInput } from "../utils/mobileInput";
import { handleRoundOffInputChange } from "../utils/roundOffInput";
import { autoRoundOff } from "../utils/autoRoundOff";
import {
  setupPortalContainers,
  teardownPortalContainers,
  getDropdownPortalTarget,
} from "../utils/portalContainers";
import { extractListRowsFromResponse } from "../utils/parseApiPagination";
import { productGroupOptionDisplayName } from "../utils/productGroupPool";

const InlineAutoCompleteInput = ({
  value,
  onChange,
  onFocus,
  onBlur,
  suggestion,
  onComplete,
  id,
  className,
  style,
  ...props
}) => {
  const displaySuggestion = suggestion ? suggestion.split(" (")[0] : "";
  const [isActive, setIsActive] = useState(false);

  const valStr = String(value ?? "");
  const hasMatchingSuffix =
    Boolean(displaySuggestion) &&
    Boolean(valStr.trim()) &&
    displaySuggestion.toLowerCase().startsWith(valStr.toLowerCase()) &&
    displaySuggestion.length > valStr.length;

  const remainingSuffix = hasMatchingSuffix
    ? displaySuggestion.slice(valStr.length)
    : "";

  return (
    <div className="position-relative" style={{ width: "100%" }}>
      <input
        type="text"
        value={value}
        onChange={onChange}
        onFocus={(e) => {
          setIsActive(true);
          onFocus && onFocus(e);
        }}
        onBlur={(e) => {
          setIsActive(false);
          onBlur && onBlur(e);
        }}
        onKeyDown={(e) => {
          if ((e.key === "Tab" || e.key === "Enter") && hasMatchingSuffix) {
            e.preventDefault();
            onComplete(displaySuggestion);
          }
        }}
        className={className}
        style={style}
        {...props}
      />
      {hasMatchingSuffix && isActive && (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            padding: style?.padding || "0.4rem 0.5rem",
            fontSize: style?.fontSize || "0.85rem",
            fontFamily: "inherit",
            lineHeight: 1.5,
            whiteSpace: "nowrap",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <span style={{ visibility: "hidden", opacity: 0 }}>{valStr}</span>
          <span style={{ color: "#9ca3af" }}>{remainingSuffix}</span>
        </div>
      )}
    </div>
  );
};

const FALLBACK_CITIES_BY_STATE = {
  MH: ["Mumbai", "Pune", "Nagpur", "Nashik", "Aurangabad", "Kolhapur"],
  GJ: ["Ahmedabad", "Surat", "Vadodara", "Rajkot", "Bhavnagar", "Jamnagar"],
  RJ: ["Jaipur", "Jodhpur", "Udaipur", "Kota", "Ajmer", "Bikaner"],
  DL: ["New Delhi", "Dwarka", "Rohini", "Saket", "Karol Bagh", "Lajpat Nagar"],
  KA: ["Bengaluru", "Mysuru", "Mangaluru", "Hubballi", "Belagavi", "Davangere"],
  TN: [
    "Chennai",
    "Coimbatore",
    "Madurai",
    "Salem",
    "Tiruchirappalli",
    "Vellore",
  ],
  TG: [
    "Hyderabad",
    "Warangal",
    "Nizamabad",
    "Khammam",
    "Karimnagar",
    "Sangareddy",
  ],
  WB: ["Kolkata", "Howrah", "Durgapur", "Siliguri", "Asansol", "Kharagpur"],
  UP: ["Lucknow", "Kanpur", "Varanasi", "Agra", "Noida", "Ghaziabad"],
  MP: ["Indore", "Bhopal", "Gwalior", "Jabalpur", "Ujjain", "Sagar"],
};

const SUMMARY_LABEL_STYLE = {
  width: "110px",
  fontSize: "0.85rem",
  marginBottom: 0,
  flexShrink: 0,
  fontWeight: 600,
};

/** Narrow labels/fields for summary block beside payment table */
const SUMMARY_COMPACT_LABEL_STYLE = {
  width: "72px",
  fontSize: "0.8rem",
  marginBottom: 0,
  flexShrink: 0,
  fontWeight: 600,
};

const SUMMARY_COMPACT_INPUT_WRAP = {
  flex: 1,
  minWidth: 0,
  maxWidth: "180px",
};

const SUMMARY_COMPACT_DISCOUNT_TYPE_SELECT_STYLE = {
  width: "52px",
  minWidth: "52px",
  flexShrink: 0,
  paddingLeft: "0.4rem",
  paddingRight: "1.1rem",
};

const SUMMARY_COMPACT_NOTE_WRAP = {
  flex: 1,
  minWidth: 0,
  maxWidth: "100%",
};

const PURCHASE_PAYMENT_METHODS = [
  "cash",
  "cheque",
  "bank_transfer",
  "upi",
  "card",
  "old_gold",
  "old_silver",
  "kasar",
  "scheme",
  "order",
  "os",
  "wallet",
];

const PURCHASE_PAYMENT_LABELS = {
  cash: "Cash",
  cheque: "Cheque",
  bank_transfer: "Bank Transfer",
  upi: "UPI",
  card: "Card",
  old_gold: "Old Gold",
  old_silver: "Old Silver",
  kasar: "Kasar",
  scheme: "Scheme",
  order: "Order",
  os: "OS",
  wallet: "Wallet",
};

/** Read-only totals styling (light blue, similar to Sales) */
const READONLY_SUMMARY_INPUT_STYLE = { backgroundColor: "#e8f4fc" };

// React-select styling so Type column matches Sales UI.
const PURCHASE_TYPE_SELECT_STYLES = {
  control: (base) => ({
    ...base,
    minHeight: "38px",
    height: "38px",
    width: "100%",
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    backgroundColor: "transparent",
    "&:hover": {
      border: "none",
      cursor: "pointer",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    height: "38px",
    display: "flex",
    alignItems: "center",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: "#000",
  }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    color: "#000",
    fontSize: "0.85rem",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
    color: "#6c757d",
    display: "none",
  }),
  indicatorSeparator: (base) => ({
    display: "none",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    fontSize: "0.85rem",
    width: "100%",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "var(--theme-primary)"
      : state.isFocused
        ? "#f8f9fa"
        : "white",
    color: state.isSelected ? "white" : "#000",
    "&:active": {
      backgroundColor: "var(--theme-primary)",
      color: "white",
    },
  }),
  container: (base) => ({
    ...base,
    width: "100%",
    height: "100%",
  }),
};

// Mode should look like Sales “plain cell”: hide dropdown arrow.
const PURCHASE_MODE_SELECT_STYLES = PURCHASE_TYPE_SELECT_STYLES;

const defaultPurchasePaymentRows = () => [
  {
    paymentId: null,
    pay_mode: "cash",
    type: "",
    remarks: "",
    amount: "",
    rowKey: "pay-0",
  },
  {
    paymentId: null,
    pay_mode: "upi",
    type: "",
    remarks: "",
    amount: "",
    rowKey: "pay-1",
  },
  {
    paymentId: null,
    pay_mode: "bank_transfer",
    type: "",
    remarks: "",
    amount: "",
    rowKey: "pay-2",
  },
];

const PurchaseRateCut = ({
  masterGoldRate,
  masterSilverRate,
  showLiveRate = true,
  pageTitle = "Rate Cut",
}) => {
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();
  const navigate = useNavigate();
  // Add new function to calculate per gram rate
  const calculatePerGramRate = (rate, metalType) => {
    return rate;
  };

  // Calculate rate based on purity
  const calculatePurityBasedRate = (purity, rate, metalType) => {
    // Extract the karat/purity value from string (e.g. "18K" -> 18 or "925" -> 925)
    const purityValue = parseFloat(purity);
    if (isNaN(purityValue)) return rate;

    if (metalType === "Gold") {
      return rate * (purityValue / 24);
    } else if (metalType === "Silver") {
      return (rate * purityValue) / 999;
    }

    return rate;
  };
  const userData = getUserData();

  const COLUMNS = [
    { id: "no", label: "No.", width: "20px", value: (row) => row.no },
    {
      id: "tagNo",
      label: "Tag No",
      width: "90px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.tagNo}
          onChange={(e) => onChange(row.id, "tagNo", e.target.value)}
        />
      ),
    },
    {
      id: "productGroup",
      label: (
        <>
          Product Group 
        </>
      ),
      width: "160px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.productGroup}
          onChange={(e) => onChange(row.id, "productGroup", e.target.value)}
        />
      ),
    },
    {
      id: "pcs",
      label: "Pcs",
      width: "50px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.pcs}
          onChange={(e) => onChange(row.id, "pcs", e.target.value)}
        />
      ),
    },
    {
      id: "grWt",
      label: "Gr.Wt",
      width: "50px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.grWt}
          onChange={(e) => onChange(row.id, "grWt", e.target.value)}
        />
      ),
    },
    {
      id: "lsWt",
      label: "Ls.Wt",
      width: "50px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lsWt}
          onChange={(e) => onChange(row.id, "lsWt", e.target.value)}
        />
      ),
    },
    {
      id: "netWt",
      label: "Net.Wt",
      width: "50px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.netWt}
          onChange={(e) => onChange(row.id, "netWt", e.target.value)}
        />
      ),
    },
    {
      id: "purity",
      label: "Purity",
      width: "60px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.purity}
          onChange={(e) => onChange(row.id, "purity", e.target.value)}
        />
      ),
    },
    {
      id: "wastage",
      label: "Wastage",
      width: "60px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.wastage}
          onChange={(e) => onChange(row.id, "wastage", e.target.value)}
        />
      ),
    },
    {
      id: "fineWt",
      label: "Fine.Wt",
      width: "60px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.fineWt}
          onChange={(e) => onChange(row.id, "fineWt", e.target.value)}
          readOnly
          style={{ backgroundColor: "#f0f0f0" }}
        />
      ),
    },
    {
      id: "itRateOn",
      label: "RateOn",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="text"
          value="FWt"
          readOnly
          style={{ backgroundColor: "#f0f0f0" }}
        />
      ),
    },
    {
      id: "itRate",
      label: "Rate",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.itRate}
          onChange={(e) => onChange(row.id, "itRate", e.target.value)}
        />
      ),
    },
    {
      id: "itAmt",
      label: "Amount",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.itAmt}
          onChange={(e) => onChange(row.id, "itAmt", e.target.value)}
        />
      ),
    },
    {
      id: "othAmt",
      label: "Other",
      width: "100px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.othAmt}
          onChange={(e) => onChange(row.id, "othAmt", e.target.value)}
        />
      ),
    },
    {
      id: "totalAmt",
      label: "TotalAmt",
      width: "100px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.totalAmt}
          onChange={(e) => onChange(row.id, "totalAmt", e.target.value)}
        />
      ),
    },
  ];

  // Fetch customers only when searching (not on pageload)
  const fetchUserss = async (search = "") => {
    const searchText =
      search !== undefined && search !== null ? String(search).trim() : "";
    if (!searchText || searchText.length < 3) return [];

    try {
      const response = await listCustomerUsers({ search: searchText });
      const fetchedUserss = mapCustomerUsersToPartyList(response);
      if (fetchedUserss?.length) {
        setUserss((prev) => {
          const byId = new Map(
            (Array.isArray(prev) ? prev : []).map((u) => [String(u.id), u]),
          );
          fetchedUserss.forEach((u) => byId.set(String(u.id), u));
          return Array.from(byId.values()).sort((a, b) => b.id - a.id);
        });
        customerSearchCacheRef.current.add(searchText.toLowerCase());
        return fetchedUserss;
      }
      return [];
    } catch (error) {
      console.error("Error fetching User Details:", error);
      return [];
    }
  };

  // Product group search via API when user types 3+ characters (Add Purchase pattern)
  const fetchProductGroupByPrefix = async (prefix = "") => {
    const trimmed = String(prefix || "").trim();
    if (trimmed.length < 3) {
      setProductGroupApiCalled(false);
      return;
    }

    if (lastSearchedProductGroupPrefix === trimmed) {
      setProductGroupApiCalled(true);
      return;
    }

    try {
      const opts = await searchProductGroupSelectOptions(trimmed);
      const uniqueProductGroups = (opts || []).map((group) => ({
        value: group.value ?? group.id,
        label: group.label || group.name || "",
        short_code: group.short_code || "",
        metal: group.metal || "",
        metal_group_id: group.metal_group_id ?? null,
        purity: group.purity || "",
      }));

      setDropdownData((prev) => ({
        ...prev,
        productGroup: mergeProductGroupOptions(
          Array.isArray(prev.productGroup) ? prev.productGroup : [],
          uniqueProductGroups,
        ),
      }));

      setLastSearchedProductGroupPrefix(trimmed);
      setProductGroupApiCalled(true);
    } catch (error) {
      console.error("Error fetching product group options:", error);
      setDropdownData((prev) => ({
        ...prev,
        productGroup: [],
      }));
      setProductGroupApiCalled(true);
    }
  };

  const fetchTagNoByPrefix = async (prefix) => {
    if (!prefix || prefix.length < 3) {
      return;
    }

    if (lastSearchedTagPrefix === prefix) {
      return;
    }

    try {
      const response = await listProducts({
        search: prefix,
      });
      let products = normalizeProductStockListResponse(response);
      if (!products.length) {
        const showRes = await showProducts({ tag_no: prefix });
        products = normalizeProductStockShowResponse(showRes);
      }

      if (products.length > 0) {
        const fetchedTags = products.map((item) => {
          if (typeof item === "string") {
            return {
              value: item,
              label: item,
              tag_no: item,
            };
          }
          return {
            value: item.id,
            label: item.tag_no,
            ...item,
          };
        });

        setDropdownData((prev) => ({
          ...prev,
          tagNo: fetchedTags,
        }));
        setLastSearchedTagPrefix(prefix);
        setTagNoApiCalled(true);
      } else {
        setDropdownData((prev) => ({
          ...prev,
          tagNo: [],
        }));
        setTagNoApiCalled(true);
      }
    } catch (error) {
      console.error("Error fetching tag no options:", error);
      setDropdownData((prev) => ({
        ...prev,
        tagNo: [],
      }));
      setTagNoApiCalled(true);
    }
  };

  const clearTagDerivedRowFields = (rowData) => {
    const clearCell = (id) => {
      const cell = rowData.find((c) => c.id === id);
      if (!cell) return;
      cell.value = "";
      if (id === "tagNo") delete cell.productId;
      if (id === "productGroup") delete cell.productGroupId;
    };
    ["productGroup", "pcs", "grWt", "lsWt", "netWt", "purity", "fineWt", "itAmt", "totalAmt"].forEach(
      clearCell,
    );
  };

  const fetchPurityForProductGroupId = async (productGroupId) => {
    if (!productGroupId) return "";
    try {
      const pgRes = await showProductGroup({
        id: Number(productGroupId),
      });
      const pg = pgRes?.data?.product_group ?? pgRes?.data;
      if (!isErpApiSuccess(pgRes) || !pg?.metal_group_id) return "";

      const mgRes = await showMetalGroup({
        id: Number(pg.metal_group_id),
      });
      const mg = mgRes?.data?.metal_group ?? mgRes?.data;
      if (!isErpApiSuccess(mgRes) || !mg) return "";

      return mg.purity ?? mg.Purity ?? mg.carat ?? mg.Carat ?? "";
    } catch (error) {
      console.error("Error fetching metal group purity:", error);
      return "";
    }
  };

  const applyPurityAndFineWtToRow = (rowData, purityValue, resetWastage = false) => {
    const purityIdx = rowData.findIndex((c) => c.id === "purity");
    if (purityIdx !== -1) {
      rowData[purityIdx] = {
        ...rowData[purityIdx],
        value:
          purityValue === null || purityValue === undefined
            ? ""
            : String(purityValue),
      };
    }
    if (resetWastage) {
      const wastageIdx = rowData.findIndex((c) => c.id === "wastage");
      if (wastageIdx !== -1) {
        rowData[wastageIdx] = { ...rowData[wastageIdx], value: "" };
      }
    }

    const netWtCell = rowData.find((c) => c.id === "netWt");
    const purityCell = rowData.find((c) => c.id === "purity");
    const wastageCell = rowData.find((c) => c.id === "wastage");
    const fineWtCell = rowData.find((c) => c.id === "fineWt");
    if (netWtCell && purityCell && fineWtCell) {
      const wastage = resetWastage ? 0 : parseFloat(wastageCell?.value) || 0;
      const fineWt = calculateFineWeight(
        netWtCell.value,
        purityCell.value,
        wastage,
      );
      const fineIdx = rowData.findIndex((c) => c.id === "fineWt");
      if (fineIdx !== -1) {
        rowData[fineIdx] = {
          ...rowData[fineIdx],
          value: fineWt.toFixed(2),
        };
      }
    }
    return rowData;
  };

  const sendTagNoToApi = async (tagNo, selectedRowIndex = null) => {
    try {
      const response = await showProducts({ tag_no: tagNo });
      const products = normalizeProductStockShowResponse(response);

      if (!products.length) return;

      const productData = products[0];
      const nestedProduct =
        (productData && typeof productData === "object" && productData.product) ||
        (productData && typeof productData === "object" && productData.data) ||
        null;
      const pick = (...keys) => {
        for (const k of keys) {
          if (
            productData &&
            Object.prototype.hasOwnProperty.call(productData, k) &&
            productData[k] !== undefined &&
            productData[k] !== null &&
            productData[k] !== ""
          ) {
            return productData[k];
          }
          if (
            nestedProduct &&
            Object.prototype.hasOwnProperty.call(nestedProduct, k) &&
            nestedProduct[k] !== undefined &&
            nestedProduct[k] !== null &&
            nestedProduct[k] !== ""
          ) {
            return nestedProduct[k];
          }
        }
        return "";
      };

      const productGroupId = pick("product_group_id", "product_group");
      let productGroupLabel =
        pick("product_group_name", "product_type") ?? productGroupId ?? "";
      let purityValue = "";

      if (productGroupId) {
        purityValue = await fetchPurityForProductGroupId(productGroupId);
        try {
          const pgRes = await showProductGroup({
            id: Number(productGroupId),
          });
          const pg = pgRes?.data?.product_group ?? pgRes?.data;
          if (isErpApiSuccess(pgRes) && pg) {
            productGroupLabel = pg.name || pg.label || productGroupLabel;
          }
        } catch (pgErr) {
          console.error("Error fetching product group details:", pgErr);
        }
      }

      const pgMatch = dropdownData.productGroup.find(
        (p) => String(p.value) === String(productGroupId),
      );
      if (pgMatch && !productGroupLabel) productGroupLabel = pgMatch.label;
      if (!purityValue && pgMatch?.purity) purityValue = pgMatch.purity;

      const productId = pick("id", "product_id");
      const pcs = pick("current_pcs", "pcs");
      const grWt = pick("current_gwt", "gwt");
      const lsWt = pick("lwt_customer", "LWT", "lwt");
      let nwt = pick("NWT", "nwt", "netWt");
      if (!nwt) {
        const g = parseFloat(grWt) || 0;
        const l = parseFloat(lsWt) || 0;
        nwt = (g - l).toFixed(2);
      }

      setTableData((prevData) => {
        const rowIndex =
          selectedRowIndex != null
            ? selectedRowIndex
            : prevData.findIndex((row) =>
              row.data.some((cell) => {
                if (cell.id !== "tagNo") return false;
                return (
                  String(cell.value || "").trim().toLowerCase() ===
                  String(tagNo || "").trim().toLowerCase()
                );
              }),
            );

        if (rowIndex === -1) return prevData;

        const newTableData = prevData.map((row, idx) => {
          if (idx !== rowIndex) return row;
          const rowData = row.data.map((cell) => {
            if (cell.id === "tagNo") {
              return {
                ...cell,
                value: String(tagNo || ""),
                ...(productId ? { productId: Number(productId) || productId } : {}),
              };
            }
            if (cell.id === "productGroup") {
              return {
                ...cell,
                value:
                  productGroupLabel === null || productGroupLabel === undefined
                    ? ""
                    : String(productGroupLabel),
                ...(productGroupId
                  ? {
                    productGroupId:
                      Number(productGroupId) || productGroupId,
                  }
                  : {}),
              };
            }
            if (cell.id === "pcs") {
              return { ...cell, value: pcs ? String(pcs) : "" };
            }
            if (cell.id === "grWt") {
              return { ...cell, value: grWt ? String(grWt) : "" };
            }
            if (cell.id === "lsWt") {
              return { ...cell, value: lsWt ? String(lsWt) : "" };
            }
            if (cell.id === "netWt") {
              return { ...cell, value: nwt ? String(nwt) : "" };
            }
            if (cell.id === "purity") {
              return {
                ...cell,
                value:
                  purityValue === null || purityValue === undefined
                    ? ""
                    : String(purityValue),
              };
            }
            if (cell.id === "wastage") {
              return { ...cell, value: "" };
            }
            return { ...cell };
          });

          applyPurityAndFineWtToRow(rowData, purityValue, true);

          return { ...row, data: rowData };
        });

        return calculateAndUpdateItRate(newTableData, rowIndex);
      });
    } catch (error) {
      console.error("Error in API call:", error);
    }
  };

  const fetchPaymentTypes = async () => {
    try {
      const response = await listBankAccounts();
      const rows = Array.isArray(response?.data?.bank_accounts)
        ? response.data.bank_accounts
        : [];

      if (response?.success && Array.isArray(rows)) {
        const fetchedBankDetails = rows.map((bank) => ({
          id: bank.id,
          bankName: bank.bank_name || "",
          accountHolder: bank.holder_name || "",
          accountType: bank.account_type || "",
          ifscCode: bank.ifsc_code || bank.IFSC_code || "",
          accountNumber: bank.account_number || "",
          openingBalance: bank.opening_balance || "",
          balance: bank.opening_balance ? `₹${bank.opening_balance}` : "₹0",
          status: bank.status === 1 ? "Active" : "Inactive",
          statusClass:
            bank.status === 1
              ? "bg-success-subtle text-success"
              : "bg-danger-subtle text-danger",
          useForInvoice: bank.use_for_invoice === 1,
          useForPOS: bank.use_for_pos === 1,
        }));

        fetchedBankDetails.sort((a, b) => b.id - a.id);
        setBankAccounts(fetchedBankDetails);

        const paymentOptions = fetchedBankDetails.map((bank) => ({
          value: bank.id,
          label: bank.accountNumber ? `${bank.bankName}` : bank.bankName,
        }));
        setPaymentTypes(paymentOptions);
      } else if (
        response.data &&
        response.data.message === "radha" &&
        (!response.data.data || response.data.data.length === 0)
      ) {
        setBankAccounts([]);
        setPaymentTypes([]);
        toast("No bank accounts found");
      } else if (response.data && response.data.success === false) {
        setBankAccounts([]);
        setPaymentTypes([]);
        toast.error(response.data.message || "Failed to fetch bank details");
      } else {
        setBankAccounts([]);
        setPaymentTypes([]);
        toast.error("Unexpected response format from server");
      }
    } catch (error) {
      setBankAccounts([]);
      setPaymentTypes([]);
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch bank details"
      );
    }
  };

  useEffect(() => {
    fetchPaymentTypes();
  }, []);
  const [allProducts, setAllProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [purchasePaymentRows, setPurchasePaymentRows] = useState(
    defaultPurchasePaymentRows,
  );

  const [payableRows, setPayableRows] = useState([
    {
      id: 1,
      pay_mode: "cash",
      type: "",
      remarks: "",
      amount: "",
    },
  ]);

  const findBankAccountByKeyword = (accounts, keyword) =>
    accounts.find(
      (b) =>
        (b.accountType &&
          (String(b.accountType).toLowerCase() === keyword ||
            String(b.accountType).toLowerCase() ===
            keyword.replace("_", " "))) ||
        (b.bankName &&
          String(b.bankName).toLowerCase().includes(keyword.replace("_", " "))),
    );

  // Match Sales.jsx: default cash row to cash ledger when banks load; Type lists all banks.
  useEffect(() => {
    if (bankAccounts.length === 0) return;
    const cashAccount = findBankAccountByKeyword(bankAccounts, "cash");
    if (!cashAccount) return;
    setPurchasePaymentRows((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (!row.type && row.pay_mode === "cash") {
          changed = true;
          return { ...row, type: String(cashAccount.id) };
        }
        return row;
      });
      return changed ? next : prev;
    });
  }, [bankAccounts]);

  const updatePurchasePaymentRow = (index, field, raw) => {
    setPurchasePaymentRows((prev) => {
      const next = [...prev];
      const cur = { ...next[index] };
      if (field === "pay_mode") {
        cur.pay_mode = raw;
        cur.type = "";
        if (raw === "cash") {
          const cashAccount = findBankAccountByKeyword(bankAccounts, "cash");
          if (cashAccount) cur.type = String(cashAccount.id);
        }
      } else if (field === "type") {
        cur.type = raw;
      } else if (field === "remarks") {
        cur.remarks = raw;
      } else if (field === "amount") {
        cur.amount = raw;
      }
      next[index] = cur;
      return next;
    });
  };

  const addPurchasePaymentRow = () => {
    setPurchasePaymentRows((prev) => [
      ...prev,
      {
        paymentId: null,
        pay_mode: "bank_transfer",
        type: "",
        remarks: "",
        amount: "",
        rowKey: `pay-n-${Date.now()}`,
      },
    ]);
  };

  // Sales-like UX: when user focuses the last row amount, add one more row.
  const handleAmountFieldFocus = (rowIdx) => {
    if (rowIdx === purchasePaymentRows.length - 1) {
      addPurchasePaymentRow();
    }
  };

  const removePurchasePaymentRow = (index) => {
    setPurchasePaymentRows((prev) =>
      prev.length <= 1 ? prev : prev.filter((_, i) => i !== index),
    );
  };

  // Demo data for dropdowns
  const [dropdownData, setDropdownData] = useState({
    productGroup: [],
    tagNo: [],
    itRateOn: ["FWt", "NWt", "GWt"], // Default is FWt; dropdown shows all 3
  });

  /** Resolve a row's product group text against loaded options (exact, then trim/case). */
  const findProductGroupMatch = (cellValue) => {
    const raw = String(cellValue ?? "").trim();
    if (!raw) return null;
    const groups = dropdownData.productGroup || [];
    const exact = groups.find(
      (g) => g.label === cellValue || g.short_code === cellValue,
    );
    if (exact) return exact;
    const nl = raw.toLowerCase();
    return (
      groups.find(
        (g) =>
          String(g.label ?? "").trim().toLowerCase() === nl ||
          String(g.short_code ?? "").trim().toLowerCase() === nl,
      ) || null
    );
  };

  // Columns that should have dropdown functionality
  const dropdownColumns = ["tagNo", "productGroup", "rateOn", "itRateOn"];

  const tagFilledReadOnlyColumns = ["productGroup", "grWt", "lsWt"];

  const rowHasTagNo = (row) =>
    Boolean(String(row?.data?.find((c) => c.id === "tagNo")?.value || "").trim());

  const isTagLockedField = (columnId, row) =>
    tagFilledReadOnlyColumns.includes(columnId) && rowHasTagNo(row);

  const isRowDropdownColumn = (columnId, row) =>
    dropdownColumns.includes(columnId) &&
    (columnId !== "productGroup" || !rowHasTagNo(row));

  const initialRowData = COLUMNS.map((col) => ({
    id: col.id,
    value: col.id === "itRateOn" ? "FWt" : "", // Set itRateOn to "FWt" by default
  }));

  const [tableData, setTableData] = useState([{ id: 1, data: initialRowData }]);
  const [itemFieldErrors, setItemFieldErrors] = useState({});

  useEffect(() => {
    setItemFieldErrors((prev) =>
      pruneLineFieldErrors(
        prev,
        collectLineItemFieldErrors(
          tableData,
          PURCHASE_RATE_CUT_LINE_ERROR_OPTIONS,
        ),
      ),
    );
  }, [tableData]);

  const [activeDropdown, setActiveDropdown] = useState({
    row: null,
    col: null,
    type: null,
  });
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [lastSearchedProductGroupPrefix, setLastSearchedProductGroupPrefix] =
    useState("");
  const [productGroupApiCalled, setProductGroupApiCalled] = useState(false); // Track if API was called to show "No matching items"
  const [lastSearchedTagPrefix, setLastSearchedTagPrefix] = useState("");
  const [tagNoApiCalled, setTagNoApiCalled] = useState(false);
  const dropdownRef = useRef(null);
  const inputRefs = useRef({});
  const dropdownItemsRef = useRef([]);
  const tooltipRefs = useRef({}); // Add refs for tooltips

  // Add state to track active tooltip
  const [activeTooltip, setActiveTooltip] = useState(null);
  const tooltipTimeoutRef = useRef(null);
  const totalAmountInputRef = useRef(null);

  // Add state for other amount modal
  const [otherAmountModal, setOtherAmountModal] = useState({
    isOpen: false,
    rowIndex: null,
    entries: [],
    currentEntry: {
      chargeType: "",
      pcs: "",
      ct: "",
      wt: "",
      conPercentage: "",
      rateOn: "",
      rate: "",
      amount: "",
    },
    editingIndex: undefined,
  });

  const [totalAmountModal, setTotalAmountModal] = useState({
    isOpen: false,
    rowIndex: null,
    amount: "",
    gst: "3",
  });

  const [totalAmountEnterPress, setTotalAmountEnterPress] = useState({
    time: 0,
    rowIndex: null,
  });

  const [lastEnterPress, setLastEnterPress] = useState({
    time: 0,
    rowIndex: null,
  });

  const [otherAmountColumnWidths, setOtherAmountColumnWidths] = useState({
    no: "10px",
    chargetype: "150px",
    pcs: "20px",
    ct: "50px",
    wt: "60px",
    con: "10px",
    rateon: "50px",
    rate: "68px",
    amount: "80px",
  });

  // Update dropdown position after Product Group API response when dropdown is active
  useEffect(() => {
    if (
      productGroupApiCalled &&
      activeDropdown.type === "productGroup" &&
      activeDropdown.row !== null &&
      activeDropdown.col !== null
    ) {
      setTimeout(() => {
        const inputElement =
          inputRefs.current[`${activeDropdown.row}-${activeDropdown.col}`];
        if (inputElement) {
          const rect = inputElement.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
          });
        }
      }, 10);
    }
  }, [productGroupApiCalled, dropdownData.productGroup, activeDropdown]);

  useEffect(() => {
    if (
      tagNoApiCalled &&
      activeDropdown.type === "tagNo" &&
      activeDropdown.row !== null &&
      activeDropdown.col !== null
    ) {
      setTimeout(() => {
        const inputElement =
          inputRefs.current[`${activeDropdown.row}-${activeDropdown.col}`];
        if (inputElement) {
          const rect = inputElement.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
          });
        }
      }, 10);
    }
  }, [tagNoApiCalled, dropdownData.tagNo, activeDropdown]);

  useEffect(() => {
    if (totalAmountModal.isOpen) {
      const timer = setTimeout(() => {
        if (totalAmountInputRef.current) {
          totalAmountInputRef.current.focus();
          totalAmountInputRef.current.select();
        }
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [totalAmountModal.isOpen]);

  // Add rateOn options
  const rateOnOptions = ["FIXED", "PCS", "CT", "WT"];

  // const [billtype, setBillType] = useState("");
  const [billDate, setBillDate] = useState("");

  // Party (Type / Name / Mobile) selection like AddJangad
  // Party type is always "KAR" (Karigar) since we only search karigar
  const partyType = "KAR";
  const [partyName, setPartyName] = useState("");
  const [partyMobile, setPartyMobile] = useState("");
  const [partyId, setPartyId] = useState(null);
  const [supplierBalances, setSupplierBalances] = useState({
    money: "",
    gold_gm: "",
    silver_gm: "",
    money_status: "",
    gold_status: "",
    silver_status: "",
  });
  const [isSupplierBalanceLoading, setIsSupplierBalanceLoading] =
    useState(false);
  const [showPartyDropdown, setShowPartyDropdown] = useState(false);
  const [partyOptions, setPartyOptions] = useState([]);
  const partyDropdownRef = useRef(null);
  const partyNameInputRef = useRef(null);
  const partyItemsRef = useRef([]);
  const [partyHighlightedIndex, setPartyHighlightedIndex] = useState(0);
  const partySearchRequestRef = useRef(0);

  const mapSupplierToPartyOption = (karigar) => {
    const displayName =
      karigar.company_name ||
      karigar.companyName ||
      karigar.name ||
      karigar.fullName ||
      karigar.supplier_name ||
      karigar.short_code ||
      (karigar.id != null ? `Supplier #${karigar.id}` : "");
    const contactNumber =
      karigar.contact_number ||
      karigar.contactNumber ||
      karigar.mobile ||
      karigar.phone ||
      "";

    let label = displayName;
    if (contactNumber) {
      label = `${displayName} (${contactNumber})`;
    }

    return {
      value: contactNumber || karigar.short_code || displayName,
      label,
      name: displayName,
      id: karigar.id,
      mobile: contactNumber || "",
    };
  };

  // Fetch karigar options by name prefix (2+ chars) - directly search karigar
  const fetchPartyOptionsByPrefix = async (prefix) => {
    const requestId = ++partySearchRequestRef.current;
    try {
      if (!prefix || prefix.length < 2) {
        setPartyOptions([]);
        setShowPartyDropdown(false);
        return;
      }

      const response = await listSuppliers({ search: prefix, status: 1 });
      if (requestId !== partySearchRequestRef.current) return;

      let options = [];
      if (isErpApiSuccess(response)) {
        const karigarList = extractListRowsFromResponse(response, ["suppliers"]);
        if (karigarList.length > 0) {
          options = karigarList
            .filter((karigar) => {
              const status = String(karigar?.status ?? "").trim().toLowerCase();
              if (!status) return true;
              return ["1", "active", "enabled", "true"].includes(status);
            })
            .map(mapSupplierToPartyOption)
            .filter((option) => option.name);
        }
      }

      setPartyOptions(options);
      setShowPartyDropdown(options.length > 0);
      setPartyHighlightedIndex(0);
    } catch (error) {
      if (requestId !== partySearchRequestRef.current) return;
      console.error("Error fetching karigar options by prefix:", error);
      setPartyOptions([]);
      setShowPartyDropdown(false);
    }
  };

  const prefillPartyFromPurchase = async (type, refId) => {
    if (!refId) return;
    try {
      const response = await listSuppliers();
      if (!isErpApiSuccess(response)) return;

      const karigarList = extractListRowsFromResponse(response, ["suppliers"]);
      const found = karigarList.find((k) => String(k.id) === String(refId));
      if (found) {
        const option = mapSupplierToPartyOption(found);
        setPartyId(found.id);
        setPartyName(option.name);
        setPartyMobile(option.mobile);
      }
    } catch (error) {
      console.error("Error pre-filling party from purchase:", error);
    }
  };

  const [tagOptions, setTagOptions] = useState([]);

  // Add customer states
  const [customerMobile, setCustomerMobile] = useState("");
  const [customerMobileSearchText, setCustomerMobileSearchText] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [customerMobileDropdownActive, setCustomerMobileDropdownActive] =
    useState(false);
  const [customerMobileHighlightedIndex, setCustomerMobileHighlightedIndex] =
    useState(0);
  const customerMobileDropdownRef = useRef(null);
  const customerMobileInputRef = useRef(null);
  const customerMobileItemsRef = useRef([]);

  const [customerName, setCustomerName] = useState("");
  const [customerNameSearchText, setCustomerNameSearchText] = useState("");
  const [customerNameDropdownActive, setCustomerNameDropdownActive] =
    useState(false);
  const [customerNameHighlightedIndex, setCustomerNameHighlightedIndex] =
    useState(0);
  const customerNameDropdownRef = useRef(null);
  const customerNameInputRef = useRef(null);
  const customerNameItemsRef = useRef([]);
  const customerSearchCacheRef = useRef(new Set());

  // Customer search — only when typing 3+ chars (no pageload list)
  useEffect(() => {
    if (selectedCustomerId) return;

    const nextSearch = [customerMobileSearchText, customerNameSearchText]
      .map((value) => (value ? String(value).trim() : ""))
      .find(
        (value) =>
          value.length >= 3 &&
          !customerSearchCacheRef.current.has(value.toLowerCase()),
      );

    if (!nextSearch) return;
    void fetchUserss(nextSearch);
  }, [
    customerMobileSearchText,
    customerNameSearchText,
    selectedCustomerId,
  ]);

  const [customerAddress, setCustomerAddress] = useState("");
  const [customerCity, setCustomerCity] = useState("");

  // Add new state for customer details modal
  const [showCustomerDetailsModal, setShowCustomerDetailsModal] =
    useState(false);
  const [customerDetails, setCustomerDetails] = useState({
    email: "",
    birthDate: "",
    anniversaryDate: "",
    companyName: "",
    companyAddress: "",
    gstNumber: "",
    aadharNo: "",
    panNo: "",
    maritalStatus: "",
    // openingBalance: "",
    // pendingBalance: "",
    balance: "",
    saleBalance: "",
    reference: "",
    state: "",
  });

  const { states, cities, selectedState, setSelectedState } = useIndiaStateCity();

  // Party (Type / Name / Mobile) handlers – search by name prefix (2+ chars)
  const handlePartyNameChange = (e) => {
    const value = e.target.value;
    setPartyName(value);
    setPartyId(null);
    setSupplierBalances({
      money: "",
      gold_gm: "",
      silver_gm: "",
      money_status: "",
      gold_status: "",
      silver_status: "",
    });

    // Clear mobile when name is cleared
    if (!value) {
      setPartyMobile("");
      setPartyOptions([]);
      setShowPartyDropdown(false);
      return;
    }

    // Require at least 2 characters before calling API
    if (value.length < 2) {
      setPartyOptions([]);
      setShowPartyDropdown(false);
      return;
    }

    // Fetch karigar data from API using prefix (no type needed)
    fetchPartyOptionsByPrefix(value);
  };

  const handlePartyNameFocus = () => {
    const input = partyNameInputRef.current;
    if (input) {
      input.style.backgroundColor = "#fff";
    }
    if (partyOptions.length > 0) {
      setShowPartyDropdown(true);
    }
  };

  const handlePartyNameBlur = () => {
    const input = partyNameInputRef.current;
    if (input) {
      input.style.backgroundColor = "transparent";
    }
    setTimeout(() => setShowPartyDropdown(false), 150);
  };

  const handlePartySelect = (option) => {
    // Use mobile number from option if available, otherwise keep empty
    const mobileNumber = option.mobile || "";
    setPartyName(option.name);
    setPartyMobile(mobileNumber); // Use actual mobile number (empty if no number)
    setPartyId(option.id);
    setSupplierBalances({
      money: "",
      gold_gm: "",
      silver_gm: "",
      money_status: "",
      gold_status: "",
      silver_status: "",
    });
    setShowPartyDropdown(false);
    setPartyHighlightedIndex(0);

    // Fetch supplier balance from rate-cut summary after selection
    const branchIdRaw = userData?.branch_id ?? userData?.branchId;
    const branch_id =
      branchIdRaw != null && branchIdRaw !== "" ? Number(branchIdRaw) : NaN;

    setIsSupplierBalanceLoading(true);
    getRateCutSummary({
      supplier_id: Number(option.id),
      ...(Number.isFinite(branch_id) && branch_id > 0 ? { branch_id } : {}),
    })
      .then((res) => {
        const summary =
          res?.data?.totals ||
          res?.data?.summary ||
          res?.summary ||
          res?.data?.supplier ||
          res?.data ||
          null;
        setSupplierBalances({
          money:
            summary?.money_value != null
              ? String(summary.money_value)
              : summary?.payable_amount != null
                ? String(summary.payable_amount)
                : summary?.balance != null
                  ? String(summary.balance)
                  : summary?.money != null
                    ? String(summary.money)
                    : "",
          gold_gm:
            summary?.gold_value != null
              ? String(summary.gold_value)
              : summary?.gold_balance_gm != null
                ? String(summary.gold_balance_gm)
                : summary?.gold_gm != null
                  ? String(summary.gold_gm)
                  : "",
          silver_gm:
            summary?.silver_value != null
              ? String(summary.silver_value)
              : summary?.silver_balance_gm != null
                ? String(summary.silver_balance_gm)
                : summary?.silver_gm != null
                  ? String(summary.silver_gm)
                  : "",
          money_status: summary?.money_status || "",
          gold_status: summary?.gold_status || "",
          silver_status: summary?.silver_status || "",
        });
      })
      .catch((err) => {
        console.error("Failed to fetch supplier balance", err);
        setSupplierBalances({
          money: "",
          gold_gm: "",
          silver_gm: "",
          money_status: "",
          gold_status: "",
          silver_status: "",
        });
      })
      .finally(() => setIsSupplierBalanceLoading(false));
  };

  const formatSettlementBadge = (
    raw,
    unitPrefix = "",
    unitSuffix = "",
    explicitStatus = "",
  ) => {
    const n = Number(raw);
    const hasValue = Number.isFinite(n) && n !== 0;
    if (explicitStatus) {
      return {
        label: explicitStatus,
        valueText: hasValue
          ? `${unitPrefix}${Math.abs(n).toFixed(unitSuffix ? 4 : 2)}${unitSuffix}`
          : "",
      };
    }
    if (!hasValue) {
      return { label: "Settled", valueText: "" };
    }
    const kind = n > 0 ? "Payable" : "Advance";
    const absVal = Math.abs(n);
    const valueText = `${unitPrefix}${absVal.toFixed(unitSuffix ? 4 : 2)}${unitSuffix}`;
    return { label: kind, valueText };
  };

  const handlePartyNameKeyDown = (e) => {
    const options = partyOptions;
    if (!options.length) return;

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        setShowPartyDropdown(true);
        setPartyHighlightedIndex((prev) => {
          const next = prev < options.length - 1 ? prev + 1 : prev;
          if (partyItemsRef.current[next]) {
            partyItemsRef.current[next].scrollIntoView({
              block: "nearest",
            });
          }
          return next;
        });
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        setPartyHighlightedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : 0;
          if (partyItemsRef.current[next]) {
            partyItemsRef.current[next].scrollIntoView({
              block: "nearest",
            });
          }
          return next;
        });
        break;
      }
      case "Enter": {
        if (showPartyDropdown && options[partyHighlightedIndex]) {
          e.preventDefault();
          handlePartySelect(options[partyHighlightedIndex]);
        }
        break;
      }
      case "Escape": {
        e.preventDefault();
        setShowPartyDropdown(false);
        break;
      }
      default:
        break;
    }
  };

  // Close party dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        partyDropdownRef.current &&
        !partyDropdownRef.current.contains(event.target) &&
        partyNameInputRef.current &&
        !partyNameInputRef.current.contains(event.target)
      ) {
        setShowPartyDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const renderPartyDropdown = () => {
    if (!showPartyDropdown || partyOptions.length === 0) return null;

    const inputElement = partyNameInputRef.current;
    if (!inputElement) return null;

    const portalTarget = getDropdownPortalTarget();
    if (!portalTarget) return null;

    const inputRect = inputElement.getBoundingClientRect();
    const inputWidth = inputElement.offsetWidth;

    partyItemsRef.current = [];

    const contentWidths = partyOptions.map((option) => {
      const tempSpan = document.createElement("span");
      tempSpan.style.fontSize = "0.85rem";
      tempSpan.style.visibility = "hidden";
      tempSpan.style.position = "absolute";
      tempSpan.style.whiteSpace = "nowrap";
      tempSpan.textContent = option.label || "";
      document.body.appendChild(tempSpan);
      const width = tempSpan.offsetWidth;
      document.body.removeChild(tempSpan);
      return width;
    });

    const maxContentWidth =
      contentWidths.length > 0 ? Math.max(...contentWidths) : 0;

    const dropdownWidth = Math.max(
      inputWidth,
      Math.min(maxContentWidth + 24, 280),
      220
    );

    return ReactDOM.createPortal(
      <div
        ref={partyDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "fixed",
          top: `${inputRect.bottom}px`,
          left: `${inputRect.left}px`,
          width: `${dropdownWidth}px`,
          minWidth: `${inputWidth}px`,
          maxWidth: `${dropdownWidth}px`,
          maxHeight: "180px",
          overflowY: "auto",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
        }}
      >
        {partyOptions.map((option, idx) => (
          <div
            key={`${option.value}-${idx}`}
            ref={(el) => (partyItemsRef.current[idx] = el)}
            className={`dropdown-item ${idx === partyHighlightedIndex ? "active" : ""
              }`}
            style={{
              cursor: "pointer",
              padding: "0.25rem 1rem",
              fontSize: "0.85rem",
              backgroundColor:
                idx === partyHighlightedIndex ? "var(--theme-primary)" : "transparent",
              color: idx === partyHighlightedIndex ? "#fff" : "inherit",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              transition: "background-color 0.2s, color 0.2s",
            }}
            onMouseEnter={() => setPartyHighlightedIndex(idx)}
            onMouseDown={(e) => {
              e.preventDefault();
              handlePartySelect(option);
            }}
          >
            {option.label}
          </div>
        ))}
      </div>,
      portalTarget
    );
  };

  // Add new state for tracking if customer is being added manually
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerSaveSuccess, setCustomerSaveSuccess] = useState(false);

  const [userss, setUserss] = useState([]);

  // Add Customer Form States
  // const [fullName, setFullName] = useState("");
  // const [mobile, setMobile] = useState("");
  // const [email, setEmail] = useState("");
  // const [city, setCity] = useState("");
  // const [address, setAddress] = useState("");
  // const [birthDate, setBirthDate] = useState("");
  // const [maritalStatus, setMaritalStatus] = useState("");
  // const [anniversaryDate, setAnniversaryDate] = useState("");
  // const [openingBalance, setOpeningBalance] = useState("");
  // const [pendingBalance, setPendingBalance] = useState("");
  // const [companyName, setCompanyName] = useState("");
  // const [companyAddress, setCompanyAddress] = useState("");
  // const [gstNumber, setGstNumber] = useState("");
  // const [status, setStatus] = useState("active");
  // const [showAddModal, setShowAddModal] = useState(false);

  // Mock customer data - replace with real API data
  const USERS = userss.map((user) => ({
    id: user.id,
    name: user.fullName,
    fullName: user.fullName,
    mobile: user.mobile,
    email: user.email,
    city: user.city,
    address: user.address,
    birthDate: user.birthDate,
    maritalStatus: user.maritalStatus,
    anniversaryDate: user.anniversaryDate,
    balance: user.balance,
    saleBalance: user.saleBalance,
    companyName: user.companyName,
    companyAddress: user.companyAddress,
    gstNumber: user.gstNumber,
    aadharNo: user.aadharNo,
    panNo: user.panNo,
    reference: user.reference,
    state: user.state,
  }));

  const [gstPercentage, setGstPercentage] = useState("3");

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setBillDate(today);
  }, []);

  const handleDateChange = (e) => {
    setBillDate(e.target.value);
  };

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const suggestionTimeoutRef = useRef(null);

  // Update the charge types data to include multiple codes
  const CHARGE_TYPES = [
    {
      name: "Hallmark Charges",
      codes: ["HC", "HM"],
    },
    {
      name: "Stone Charges",
      codes: ["SC"],
    },
    {
      name: "Diamond Charges",
      codes: ["DC"],
    },
    {
      name: "Jadtar Charges",
      codes: ["JC"],
    },
    {
      name: "Customization Charges",
      codes: ["CC"],
    },
    {
      name: "Engraving Charges",
      codes: ["EC"],
    },
    {
      name: "Polishing Charges",
      codes: ["PC"],
    },
    {
      name: "Electroplating Charges",
      codes: ["EPC"],
    },
    {
      name: "Antique/Oxidized Finish Charges",
      codes: ["AFC"],
    },
    {
      name: "Kadha/Hollow Jewellery Filling Charges",
      codes: ["KFC"],
    },
    {
      name: "Melting & Refining Charges",
      codes: ["MRC"],
    },
    {
      name: "Weighing & Testing Charges",
      codes: ["WTC"],
    },
    {
      name: "Laser Cutting Charges",
      codes: ["LCC"],
    },
    {
      name: "Handmade Labour Charges",
      codes: ["HLC"],
    },
    {
      name: "Antique Kundan Meena Charges",
      codes: ["AKMC"],
    },
    {
      name: "Threading & Bead Stringing Charges",
      codes: ["TBC"],
    },
    {
      name: "Enamel Meenakari Charges",
      codes: ["EMC"],
    },
    {
      name: "Embossing & Carving Charges",
      codes: ["ECC"],
    },
    {
      name: "Rhodium Charges",
      codes: ["RC"],
    },
  ];

  // Modify the getSuggestions function to handle multiple codes
  const getSuggestions = (query) => {
    if (!query || query.length < 1) {
      setSuggestions([]);
      return;
    }

    const searchQuery = query.toLowerCase();

    // First try to match by any code
    let matchedByCode = CHARGE_TYPES.filter((charge) =>
      charge.codes.some((code) => code.toLowerCase() === searchQuery)
    );

    // If no exact code match, then try partial matches on codes
    if (matchedByCode.length === 0) {
      matchedByCode = CHARGE_TYPES.filter((charge) =>
        charge.codes.some((code) => code.toLowerCase().startsWith(searchQuery))
      );
    }

    // Get matches by name
    const matchedByName = CHARGE_TYPES.filter(
      (charge) =>
        charge.name.toLowerCase().includes(searchQuery) &&
        !matchedByCode.some((codeMatch) => codeMatch.name === charge.name)
    );

    // Combine results with code matches first
    const matchedSuggestions = [...matchedByCode, ...matchedByName].map(
      (charge) => `${charge.name} (${charge.codes[0]})`
    );

    setSuggestions(matchedSuggestions);
    setShowSuggestions(true);
    setSelectedSuggestionIndex(0);
  };

  const handleSuggestionSelect = (suggestion, rowIndex = null) => {
    const chargeName = suggestion.split(" (")[0];

    if (rowIndex !== null) {
      const newEntries = [...otherAmountModal.entries];
      newEntries[rowIndex].chargeType = chargeName;
      setOtherAmountModal((prev) => ({
        ...prev,
        entries: newEntries,
      }));
    } else {
      setOtherAmountModal((prev) => ({
        ...prev,
        currentEntry: {
          ...prev.currentEntry,
          chargeType: chargeName,
        },
      }));
    }
    setShowSuggestions(false);
  };

  // Reset highlighted index when dropdown opens or filter changes
  useEffect(() => {
    if (activeDropdown.row !== null && activeDropdown.col !== null) {
      setHighlightedIndex(0);
      dropdownItemsRef.current = [];
    }
  }, [activeDropdown, tableData]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (dropdownRef.current && dropdownItemsRef.current[highlightedIndex]) {
      dropdownItemsRef.current[highlightedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown({ row: null, col: null, type: null });
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setupPortalContainers();
    return teardownPortalContainers;
  }, []);

  // Add this function to calculate Net Weight
  const calculateNetWeight = (gw, lw) => {
    return gw - lw; // NWt = GWt - LWt
  };

  const calculateChargeableWeight = (netWt, purity, wastage) => {
    const n = parseFloat(netWt) || 0;
    const p = parseFloat(purity) || 0;
    const w = parseFloat(wastage) || 0;
    return n * ((p + w) / 100);
  };

  const calculateFineWeight = (netWt, purity, wastage) => {
    const n = parseFloat(netWt) || 0;
    const p = parseFloat(purity) || 0;
    const w = parseFloat(wastage) || 0;
    return n * ((p + w) / 100); // FineWt = [(purity + wastage)/100] * netWt
  };

  // Helper function to calculate and update itRate for a row based on product group
  const calculateAndUpdateItRate = (tableData, rowIndex) => {
    let newTableData = [...tableData];
    const productGroupCell = newTableData[rowIndex].data.find(
      (cell) => cell.id === "productGroup"
    );

    if (!productGroupCell || !productGroupCell.value) {
      return newTableData;
    }

    const selectedProductGroup = findProductGroupMatch(productGroupCell.value);

    if (selectedProductGroup) {
      let purityBasedRate = 0;
      if (selectedProductGroup.metal === "Gold") {
        if (Array.isArray(masterGoldRate)) {
          // Array contains rates
          // When showLiveRate is true: rates are per 10 grams, so divide by 10
          // When showLiveRate is false: rates are already per gram, use as is
          const label = (selectedProductGroup.label || "").toLowerCase();
          const karatMatch = label.match(/(\d+)\s*c/) || label.match(/(\d+)c/);
          let karat = karatMatch ? parseInt(karatMatch[1]) : 24;

          // Handle rose gold - use index 3 if available, otherwise calculate from 24K
          if (label.includes("rose")) {
            if (masterGoldRate[3]) {
              // If rose gold rate exists in array
              if (showLiveRate) {
                // Live rate is per 10 grams, divide by 10
                purityBasedRate = masterGoldRate[3] / 10;
              } else {
                // DB rate is already per gram
                purityBasedRate = masterGoldRate[3];
              }
            } else {
              // Calculate from 24K base rate
              const baseRate = masterGoldRate[0] || 0;
              const baseRatePerGram = showLiveRate ? baseRate / 10 : baseRate;
              purityBasedRate = baseRatePerGram * (18 / 24); // Rose gold is typically 18K
            }
          } else {
            // Use base 24K rate and calculate: (baseRate) * (karat/24)
            const baseRate = masterGoldRate[0] || 0;
            // Convert to per gram if live rate (divide by 10), otherwise use as is
            const baseRatePerGram = showLiveRate ? baseRate / 10 : baseRate;
            purityBasedRate = baseRatePerGram * (karat / 24);
          }
        } else {
          // masterGoldRate is a single value
          // When showLiveRate is true: rate is per 10 grams, so divide by 10
          // When showLiveRate is false: rate is already per gram, use as is
          const ratePerGram = showLiveRate
            ? masterGoldRate / 10
            : masterGoldRate;
          const label = (selectedProductGroup.label || "").toLowerCase();
          const karatMatch = label.match(/(\d+)\s*c/) || label.match(/(\d+)c/);
          const karat = karatMatch ? parseInt(karatMatch[1]) : 24;
          purityBasedRate = ratePerGram * (karat / 24);
        }
      } else {
        // Silver: masterSilverRate
        // When showLiveRate is true: rate is per 1000 grams (1 kg), so divide by 1000
        // When showLiveRate is false: rate is already per gram, use as is
        const ratePerGram = showLiveRate
          ? masterSilverRate / 1000
          : masterSilverRate;
        purityBasedRate = ratePerGram;
      }

      // Find and update the itRate cell
      const itRateCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "itRate"
      );

      if (itRateCell) {
        const isOverridden = newTableData[rowIndex].userOverrodeItRate;
        const effectiveRate = isOverridden
          ? parseFloat(itRateCell.value) || 0
          : purityBasedRate;
        if (!isOverridden) {
          itRateCell.value = purityBasedRate.toFixed(2);
        }

        // Recalculate itAmt after updating/using effective itRate
        const itRateOnCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "itRateOn"
        );
        const fineWtCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "fineWt"
        );
        const netWtCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "netWt"
        );
        const grWtCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "grWt"
        );

        if (fineWtCell || netWtCell || grWtCell) {
          const itRateOn = (itRateOnCell?.value || "FWt").toString();
          const baseWeight =
            itRateOn === "NWt"
              ? parseFloat(netWtCell?.value) || 0
              : itRateOn === "GWt"
                ? parseFloat(grWtCell?.value) || 0
                : parseFloat(fineWtCell?.value) || 0;
          const itAmt = (baseWeight * effectiveRate).toFixed(2);
          const itAmtCell = newTableData[rowIndex].data.find(
            (cell) => cell.id === "itAmt"
          );
          if (itAmtCell) {
            itAmtCell.value = itAmt.toString();
          }

          // Recalculate total amount
          calculateTotalAmount(newTableData, rowIndex);
        }
      }
    }

    return newTableData;
  };

  const clearManualAmountOverrides = (row) => {
    if (!row?.data) return { totalCleared: false };

    const totalAmtCell = row.data.find((cell) => cell.id === "totalAmt");
    let totalCleared = false;
    if (totalAmtCell?.totalAmountDetails?.isManual) {
      delete totalAmtCell.totalAmountDetails;
      totalCleared = true;
    }

    return { totalCleared };
  };

  const handleInputChange = (rowIndex, colIndex, value) => {
    let newTableData = [...tableData];
    const cell = newTableData[rowIndex].data[colIndex];
    const id = cell.id;

    // Allow manual override of itRate; mark flag and recalc itAmt
    if (id === "itRate") {
      cell.value = value;
      newTableData[rowIndex].userOverrodeItRate = true;
      const manualStatus = clearManualAmountOverrides(newTableData[rowIndex]);
      const itRate = parseFloat(value) || 0;
      const itRateOnCell = newTableData[rowIndex].data.find(
        (c) => c.id === "itRateOn"
      );
      const fineWtCell = newTableData[rowIndex].data.find((c) => c.id === "fineWt");
      const netWtCell = newTableData[rowIndex].data.find((c) => c.id === "netWt");
      const grWtCell = newTableData[rowIndex].data.find((c) => c.id === "grWt");
      const itAmtCell = newTableData[rowIndex].data.find(
        (c) => c.id === "itAmt"
      );
      if (itAmtCell) {
        const itRateOn = (itRateOnCell?.value || "FWt").toString();
        const baseWeight =
          itRateOn === "NWt"
            ? parseFloat(netWtCell?.value) || 0
            : itRateOn === "GWt"
              ? parseFloat(grWtCell?.value) || 0
              : parseFloat(fineWtCell?.value) || 0;
        itAmtCell.value = (baseWeight * itRate).toFixed(2);
        calculateTotalAmount(newTableData, rowIndex);
      }
      if (manualStatus.totalCleared) {
        calculateTotalAmount(newTableData, rowIndex);
      }
      setTableData(newTableData);
      return;
    }

    // Remove obsolete lbrType handling

    // Check if value matches the validation pattern for this column
    const validationPatterns = {
      pcs: /^\d*$/, // Only numbers
      purity: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      wastage: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      grWt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      lsWt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      netWt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      itRate: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      itAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      othAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      totalAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
    };

    // If pattern exists and value doesn't match, don't update
    if (validationPatterns[id] && !validationPatterns[id].test(value)) {
      return; // Don't update if validation fails
    }

    // Handle tagNo: fetch options when user types 3+ characters
    if (id === "tagNo") {
      cell.value = value;
      if (!value || !String(value).trim()) {
        clearTagDerivedRowFields(newTableData[rowIndex].data);
      }
      setTableData(newTableData);

      if (value && value.length >= 3) {
        setTagNoApiCalled(false);
        fetchTagNoByPrefix(value);
      } else {
        setLastSearchedTagPrefix("");
        setTagNoApiCalled(false);
        setDropdownData((prev) => ({
          ...prev,
          tagNo: [],
        }));
      }
      return;
    }

    if (isTagLockedField(id, newTableData[rowIndex])) {
      return;
    }

    if (id === "productGroup") {
      cell.value = value;
      setTableData(newTableData);

      if (value && value.length >= 3) {
        if (
          !lastSearchedProductGroupPrefix ||
          value !== lastSearchedProductGroupPrefix
        ) {
          setProductGroupApiCalled(false);
          fetchProductGroupByPrefix(value);
        }
      } else {
        setLastSearchedProductGroupPrefix("");
        setProductGroupApiCalled(false);
      }
      return;
    }

    cell.value = value;

    const shouldClearManualOverrides = [
      "grWt",
      "lsWt",
      "netWt",
      "pcs",
      "itRateOn",
      "itRate",
    ].includes(id);

    if (shouldClearManualOverrides) {
      const manualStatus = clearManualAmountOverrides(newTableData[rowIndex]);
      if (manualStatus.totalCleared) {
        calculateTotalAmount(newTableData, rowIndex);
      }
    }

    // Helper function to calculate and update fineWt
    const calculateAndUpdateFineWt = (tableData, rowIndex) => {
      const netWtCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "netWt"
      );
      const purityCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "purity"
      );
      const wastageCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "wastage"
      );
      const fineWtCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "fineWt"
      );

      if (netWtCell && purityCell && wastageCell && fineWtCell) {
        const netWt = parseFloat(netWtCell.value) || 0;
        const purity = parseFloat(purityCell.value) || 0;
        const wastage = parseFloat(wastageCell.value) || 0;
        const fineWt = calculateFineWeight(netWt, purity, wastage);
        fineWtCell.value = fineWt.toFixed(2);
      }
    };

    // Handle calculations based on the changed field
    if (id === "grWt" || id === "lsWt") {
      // Calculate Net Weight when Gross Weight or Less Weight changes
      const gwCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "grWt"
      );
      const lwCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "lsWt"
      );

      if (gwCell && lwCell) {
        const grWt = parseFloat(gwCell.value) || 0;
        const lsWt = parseFloat(lwCell.value) || 0;
        const netWt = calculateNetWeight(grWt, lsWt);

        // Update the netWt cell
        const netWtCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "netWt"
        );
        if (netWtCell) {
          netWtCell.value = netWt.toFixed(2);

          // Calculate fineWt
          calculateAndUpdateFineWt(newTableData, rowIndex);

          // Get fineWt for calculations
          const fineWtCell = newTableData[rowIndex].data.find(
            (cell) => cell.id === "fineWt"
          );

          // If itRate exists, recalculate itAmt based on fineWt
          const itRateCell = newTableData[rowIndex].data.find(
            (cell) => cell.id === "itRate"
          );

          if (itRateCell && itRateCell.value && fineWtCell) {
            const itRate = parseFloat(itRateCell.value) || 0;
            const fineWt = parseFloat(fineWtCell.value) || 0;
            const itAmt = (fineWt * itRate).toFixed(2);

            // Update the itAmt cell
            const itAmtCell = newTableData[rowIndex].data.find(
              (cell) => cell.id === "itAmt"
            );
            if (itAmtCell) {
              itAmtCell.value = itAmt.toString();
              calculateTotalAmount(newTableData, rowIndex);
            }
          }
        }
      }
    } else if (id === "netWt" || id === "purity" || id === "wastage") {
      // Calculate fineWt when netWt, purity, or wastage changes
      calculateAndUpdateFineWt(newTableData, rowIndex);

      // Get fineWt for calculations
      const fineWtCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "fineWt"
      );

      // Recalculate itRate first (to ensure it's up to date), then calculate itAmt
      const updatedData = calculateAndUpdateItRate(newTableData, rowIndex);
      // Update the state with the updated data
      newTableData = updatedData;

      // Recalculate itAmt using fineWt
      if (fineWtCell) {
        const itRateCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "itRate"
        );
        const itAmtCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "itAmt"
        );

        if (itRateCell && itAmtCell && itRateCell.value) {
          const itRate = parseFloat(itRateCell.value) || 0;
          const fineWt = parseFloat(fineWtCell.value) || 0;
          itAmtCell.value = (fineWt * itRate).toFixed(2);
          calculateTotalAmount(newTableData, rowIndex);
        }
      }

    } else if (id === "itAmt" || id === "othAmt") {
      calculateTotalAmount(newTableData, rowIndex);
    }

    setTableData(newTableData);

    // REMOVED THE PROBLEMATIC CODE THAT WAS CALLING sendTagNoToApi ON EVERY KEYSTROKE
    // The API call should only happen when selecting from dropdown (handled in handleDropdownSelect)

    // If you need to handle barcode scanning, you might want to add a separate function
    // or detect when the input is coming from a barcode scanner vs manual typing
  };

  // Add this function to handle key events on input fields
  const handleKeyPress = (event, rowIndex, colIndex) => {
    // Handle key press events if needed
  };

  // New function to calculate total amount
  const calculateTotalAmount = (tableData, rowIndex) => {
    const itAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "itAmt"
    );
    const othAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "othAmt"
    );
    const itAmt = parseFloat(itAmtCell?.value) || 0;
    const othAmt = parseFloat(othAmtCell?.value) || 0;
    const totalAmt = (itAmt + othAmt).toFixed(2);

    // Update the totalAmt cell
    const totalAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "totalAmt"
    );
    if (totalAmtCell) {
      if (!totalAmtCell.totalAmountDetails?.isManual) {
        totalAmtCell.value = totalAmt.toString();
        if (totalAmtCell.totalAmountDetails) {
          delete totalAmtCell.totalAmountDetails;
        }
      }
    }
  };

  const handleAddRow = () => {
    // Flag to prevent multiple simultaneous calls
    if (window.isAddingRow) return;
    window.isAddingRow = true;

    // Get the current length before adding
    const newRowIndex = tableData.length;

    setTableData((prevData) => [
      ...prevData,
      {
        id: prevData.length + 1,
        data: COLUMNS.map((col) => ({
          id: col.id,
          value: col.id === "itRateOn" ? "FWt" : "", // Set itRateOn to "FWt" by default
        })),
      },
    ]);

    // Focus on the first editable field (metal group) of the newly added row
    setTimeout(() => {
      const firstEditableIndex = COLUMNS.findIndex((col) => col.id !== "no");
      if (firstEditableIndex !== -1) {
        const firstInput =
          inputRefs.current[`${newRowIndex}-${firstEditableIndex}`];
        if (firstInput) {
          firstInput.focus();
        }
      }
      // Reset the flag after the row is added and focus is set
      window.isAddingRow = false;
    }, 150); // Increased timeout to ensure DOM is updated
  };

  // Function to focus on the first cell of the next row
  const focusFirstCellOfNextRow = (currentRowIndex) => {
    setTimeout(() => {
      const nextRowIndex = currentRowIndex + 1;
      if (nextRowIndex < tableData.length) {
        const firstCellInput = inputRefs.current[`${nextRowIndex}-0`];
        if (firstCellInput) firstCellInput.focus();
      }
    }, 0);
  };

  const handleDropdownClick = (rowIndex, colIndex, columnType) => {
    // Get position of the input element for positioning the dropdown
    const inputElement = inputRefs.current[`${rowIndex}-${colIndex}`];
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }

    setActiveDropdown({ row: rowIndex, col: colIndex, type: columnType });
    setHighlightedIndex(0);
  };

  const handleDropdownSelect = (rowIndex, colIndex, selectedItem) => {
    // Ensure we're passing the label or value, not the entire object
    const value =
      typeof selectedItem === "object" ? selectedItem.label : selectedItem;

    handleInputChange(rowIndex, colIndex, value);
    setActiveDropdown({ row: null, col: null, type: null });

    const columnId = COLUMNS[colIndex]?.id;
    if (columnId === "tagNo") {
      sendTagNoToApi(value, rowIndex);
    }

    if (columnId === "productGroup") {
      const pgId =
        typeof selectedItem === "object" ? selectedItem.value : null;
      if (pgId && !rowHasTagNo(tableData[rowIndex])) {
        fetchPurityForProductGroupId(pgId).then((purityValue) => {
          setTableData((prevData) => {
            const newData = prevData.map((row, idx) => {
              if (idx !== rowIndex) return row;
              const rowData = row.data.map((cell) => {
                if (cell.id === "productGroup") {
                  return {
                    ...cell,
                    value: String(value || ""),
                    productGroupId: Number(pgId) || pgId,
                  };
                }
                return { ...cell };
              });
              applyPurityAndFineWtToRow(rowData, purityValue, true);
              return { ...row, data: rowData };
            });
            return calculateAndUpdateItRate(newData, rowIndex);
          });
        });
      } else {
        setTableData((prevData) => calculateAndUpdateItRate(prevData, rowIndex));
      }
    }

    // Focus on the next cell after selection
    setTimeout(() => {
      const nextColIndex = colIndex + 1;
      if (nextColIndex < COLUMNS.length) {
        const nextInput = inputRefs.current[`${rowIndex}-${nextColIndex}`];
        if (nextInput) nextInput.focus();
      } else {
        // If it's the last cell in the row, focus on the first cell of the next row
        focusFirstCellOfNextRow(rowIndex);
      }
    }, 0);
  };

  // Track the last processed tag number to prevent duplicate processing
  let lastProcessedTagNo = {
    value: null,
    timestamp: 0,
  };

  // Get filtered items based on input value and column type
  const getFilteredItems = (searchText, columnType) => {
    // console.log("Column Type:", columnType);
    // console.log("Dropdown Data:", dropdownData);
    // console.log("Search Text:", searchText);

    if (!dropdownData[columnType]) return [];

    // Normalize search text
    const normalizedSearchText = searchText
      ? String(searchText).toLowerCase()
      : "";

    // If no search, return all
    if (!normalizedSearchText) return dropdownData[columnType];

    // Special case: array of strings
    if (columnType === "itRateOn") {
      // Always show all RateOn options (FWt/NWt/GWt), regardless of current cell text.
      return dropdownData[columnType];
    }

    // Special case: productGroup (search label + short_code)
    if (columnType === "productGroup") {
      return dropdownData[columnType].filter((item) => {
        const label =
          typeof item.label === "string"
            ? item.label.toLowerCase()
            : item.label != null
              ? String(item.label).toLowerCase()
              : "";
        const shortCode =
          item.short_code != null ? String(item.short_code).toLowerCase() : "";
        return (
          label.includes(normalizedSearchText) ||
          shortCode.includes(normalizedSearchText)
        );
      });
    }

    // Special case: tagNo (search only label)
    if (columnType === "tagNo") {
      return dropdownData[columnType].filter(
        (item) =>
          item.label &&
          String(item.label).toLowerCase().includes(normalizedSearchText),
      );
    }

    // Default handling
    return dropdownData[columnType].filter((item) => {
      if (typeof item === "object") {
        return item.label
          ? item.label.toLowerCase().includes(normalizedSearchText)
          : false;
      }
      return String(item).toLowerCase().includes(normalizedSearchText);
    });
  };

  // Add this function to get filtered items for Rate On dropdown
  const getFilteredRateOnItems = (searchText) => {
    if (!searchText) return rateOnOptions;
    return rateOnOptions.filter((item) =>
      item.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, rowIndex, colIndex, columnType) => {
    // Handle Tab key to move to next cell and potentially add a new row
    if (e.key === "Tab" && !e.shiftKey) {
      // If we're at the last column of the last row
      if (
        colIndex === COLUMNS.length - 1 &&
        rowIndex === tableData.length - 1
      ) {
        e.preventDefault();
        handleAddRow();
        // Focus will be set to the first cell of the new row after render
        setTimeout(() => {
          const newRowIndex = tableData.length;
          const firstCellInput = inputRefs.current[`${newRowIndex}-0`];
          if (firstCellInput) firstCellInput.focus();
        }, 0);
      }
    }

    // Handle dropdown keyboard navigation
    if (columnType === "rateOn" || dropdownColumns.includes(columnType)) {
      const cell = tableData[rowIndex]?.data[colIndex];
      if (!cell) return;

      const filtered =
        columnType === "rateOn"
          ? getFilteredRateOnItems(cell.value)
          : getFilteredItems(cell.value, columnType);

      if (filtered.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filtered.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case "Enter":
          e.preventDefault();
          if (filtered[highlightedIndex]) {
            handleDropdownSelect(
              rowIndex,
              colIndex,
              filtered[highlightedIndex]
            );
          }
          break;
        case "Escape":
          e.preventDefault();
          setActiveDropdown({ row: null, col: null, type: null });
          break;
        case "Tab":
          setActiveDropdown({ row: null, col: null, type: null });
          break;
        default:
          break;
      }
    }
  };

  // Render dropdown through portal
  const renderDropdown = () => {
    if (
      activeDropdown.row === null ||
      activeDropdown.col === null ||
      !activeDropdown.type
    )
      return null;

    const cell = tableData[activeDropdown.row]?.data[activeDropdown.col];
    if (!cell) return null;

    const filtered = getFilteredItems(cell.value, activeDropdown.type);

    // Special styling for productGroup dropdown
    const isProductGroup = activeDropdown.type === "productGroup";
    const isTagNo = activeDropdown.type === "tagNo";
    const VISIBLE_COUNT = isProductGroup || isTagNo ? 6 : 5;
    const ITEM_HEIGHT = 35;
    const visibleItems = Math.min(filtered.length, VISIBLE_COUNT);
    const dropdownHeight = visibleItems * ITEM_HEIGHT;

    // Calculate width for productGroup dropdown
    const dropdownWidth = isProductGroup || isTagNo
      ? Math.max(200, dropdownPosition.width)
      : dropdownPosition.width;

    // Reset refs array
    dropdownItemsRef.current = [];

    return ReactDOM.createPortal(
      <div
        ref={dropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownWidth}px`,
          maxHeight: `${dropdownHeight}px`,
          height: `${dropdownHeight}px`,
          overflow: "hidden",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          backgroundColor: "#fff",
          boxSizing: "border-box",
          boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
        }}
      >
        <div
          style={{
            maxHeight: `${dropdownHeight}px`,
            height: `${dropdownHeight}px`,
            overflowY: filtered.length > VISIBLE_COUNT ? "auto" : "hidden",
            overflowX: "hidden",
          }}
        >
          {filtered.length > 0 ? (
            filtered.map((item, idx) => {
              // Handle both object and string types
              const itemLabel =
                typeof item === "object"
                  ? isProductGroup
                    ? productGroupOptionDisplayName(item)
                    : item.label || ""
                  : item;
              const isSelected =
                typeof item === "object"
                  ? (item.label || "").toLowerCase() ===
                  (cell.value || "").toLowerCase()
                  : item.toLowerCase() === (cell.value || "").toLowerCase();

              return (
                <div
                  key={idx}
                  ref={(el) => (dropdownItemsRef.current[idx] = el)}
                  className={`dropdown-item ${idx === highlightedIndex ? "active" : ""
                    }`}
                  title={itemLabel}
                  style={{
                    cursor: "pointer",
                    padding: "0.4rem 0.75rem",
                    fontSize: "0.85rem",
                    backgroundColor:
                      idx === highlightedIndex
                        ? "var(--theme-primary)"
                        : isSelected
                          ? "#f0f0f0"
                          : "transparent",
                    color: idx === highlightedIndex ? "#fff" : "inherit",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onMouseDown={() =>
                    handleDropdownSelect(
                      activeDropdown.row,
                      activeDropdown.col,
                      item
                    )
                  }
                >
                  {itemLabel}
                </div>
              );
            })
          ) : (
            <div
              className="dropdown-item disabled"
              style={{
                padding: "0.4rem 0.75rem",
                fontSize: "0.85rem",
                textAlign: "center",
              }}
            >
              No matching items
            </div>
          )}
        </div>
      </div>,
      document.getElementById("dropdown-portal")
    );
  };

  // Check if a column should have dropdown functionality
  const isDropdownColumn = (columnId) => {
    return dropdownColumns.includes(columnId);
  };

  // Get column type from index
  const getColumnType = (colIndex) => {
    return COLUMNS[colIndex]?.id;
  };

  // Add a function to handle focus on the last column
  const handleFocus = (rowIndex, colIndex) => {
    // Change background color
    const inputElement = inputRefs.current[`${rowIndex}-${colIndex}`];
    if (inputElement) {
      inputElement.style.backgroundColor = "#fff";
    }

    // If this is the last column of the last row, add a new row
    if (colIndex === COLUMNS.length - 1 && rowIndex === tableData.length - 1) {
      handleAddRow();
    }

    const columnType = getColumnType(colIndex);
    const row = tableData[rowIndex];
    if (row && isRowDropdownColumn(columnType, row)) {
      if (columnType === "tagNo" || columnType === "productGroup") {
        const cell = row.data?.find((c) => c.id === columnType);
        const cellValue = (cell?.value || "").toString().trim();
        const hasEnoughChars = cellValue.length >= 3;
        const hasResults = getFilteredItems(cellValue, columnType).length > 0;
        const apiCalled =
          columnType === "productGroup"
            ? productGroupApiCalled
            : tagNoApiCalled;

        if (hasEnoughChars && (hasResults || apiCalled)) {
          handleDropdownClick(rowIndex, colIndex, columnType);
        }
      } else {
        handleDropdownClick(rowIndex, colIndex, columnType);
      }
    } else {
      // Non-dropdown column: ensure dropdown is closed
      setActiveDropdown({ row: null, col: null, type: null });
    }
  };

  const handleOtherAmountInteraction = (rowIndex, event) => {
    if (event.type === "dblclick") {
      event.preventDefault();
      openOtherAmountModal(rowIndex);
    } else if (event.type === "keydown" && event.key === "Enter") {
      const now = Date.now();
      if (
        lastEnterPress.rowIndex === rowIndex &&
        now - lastEnterPress.time < 500
      ) {
        event.preventDefault();
        openOtherAmountModal(rowIndex);
        setLastEnterPress({ time: 0, rowIndex: null });
      } else {
        setLastEnterPress({ time: now, rowIndex });
      }
    } else if (event.type === "click") {
      const tooltipId = `oth-amt-tooltip-${rowIndex}`;
      const tooltip = tooltipRefs.current[tooltipId];

      if (tooltip) {
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
        }

        if (tooltip.style.display === "block") {
          tooltipTimeoutRef.current = setTimeout(() => {
            tooltip.style.display = "none";
            setActiveTooltip(null);
          }, 4000);
          return;
        }

        tooltip.style.display = "block";
        setActiveTooltip(tooltipId);

        tooltipTimeoutRef.current = setTimeout(() => {
          tooltip.style.display = "none";
          setActiveTooltip(null);
        }, 4000);
      }
    }
  };

  const handleTotalAmountInteraction = (rowIndex, event) => {
    if (event.type === "dblclick") {
      event.preventDefault();
      openTotalAmountModal(rowIndex);
    } else if (event.type === "keydown" && event.key === "Enter") {
      const now = Date.now();
      if (
        totalAmountEnterPress.rowIndex === rowIndex &&
        now - totalAmountEnterPress.time < 500
      ) {
        event.preventDefault();
        openTotalAmountModal(rowIndex);
        setTotalAmountEnterPress({ time: 0, rowIndex: null });
      } else {
        setTotalAmountEnterPress({ time: now, rowIndex });
      }
    }
  };

  const openTotalAmountModal = (rowIndex) => {
    const row = tableData[rowIndex];
    if (!row) return;

    const totalAmtCell = row.data.find((cell) => cell.id === "totalAmt");
    const currentDetails = totalAmtCell?.totalAmountDetails;

    const currentAmount = currentDetails?.inclusiveAmount
      ? String(currentDetails.inclusiveAmount)
      : totalAmtCell &&
        totalAmtCell.value !== undefined &&
        totalAmtCell.value !== null
        ? String(totalAmtCell.value)
        : "";

    const currentGst = currentDetails?.gst ?? totalAmtCell?.gst ?? "3";

    setTotalAmountModal({
      isOpen: true,
      rowIndex,
      amount: currentAmount,
      gst: String(currentGst || "3"),
    });
  };

  const closeTotalAmountModal = (shouldRefocus = false) => {
    const { rowIndex } = totalAmountModal;

    setTotalAmountModal({
      isOpen: false,
      rowIndex: null,
      amount: "",
      gst: "3",
    });

    if (shouldRefocus && rowIndex !== null) {
      const totalAmtColIndex = COLUMNS.findIndex(
        (col) => col.id === "totalAmt"
      );
      if (totalAmtColIndex !== -1) {
        setTimeout(() => {
          const inputElement =
            inputRefs.current[`${rowIndex}-${totalAmtColIndex}`];
          if (inputElement) {
            inputElement.focus();
            inputElement.select();
          }
        }, 0);
      }
    }
  };

  const handleSaveTotalAmount = () => {
    const { rowIndex, amount, gst } = totalAmountModal;
    if (rowIndex === null) {
      closeTotalAmountModal();
      return;
    }

    const sanitizedAmount =
      typeof amount === "string" ? amount.trim() : String(amount || "");

    const gstPercentage = parseFloat(gst) || 0;
    const numericAmount = parseFloat(sanitizedAmount);
    const hasValidAmount = sanitizedAmount !== "" && !isNaN(numericAmount);

    const divisor = 1 + gstPercentage / 100;
    const baseAmount =
      hasValidAmount && divisor !== 0
        ? (numericAmount / divisor).toFixed(2)
        : "";
    const inclusiveFormatted = hasValidAmount ? numericAmount.toFixed(2) : "";

    setTableData((prevData) => {
      return prevData.map((row, idx) => {
        if (idx !== rowIndex) return row;

        const othAmtCell = row.data.find((c) => c.id === "othAmt");
        const currentOth = parseFloat(othAmtCell?.value) || 0;

        const manualBaseNumeric =
          hasValidAmount && baseAmount !== "" ? parseFloat(baseAmount) : null;

        const updatedRowData = row.data.map((cell) => {
          if (cell.id === "totalAmt") {
            const updatedCell = {
              ...cell,
            };

            if (!hasValidAmount) {
              updatedCell.value = "";
              if (updatedCell.totalAmountDetails) {
                delete updatedCell.totalAmountDetails;
              }
            } else {
              updatedCell.value = baseAmount;
              updatedCell.totalAmountDetails = {
                gst,
                inclusiveAmount: inclusiveFormatted,
                baseAmount,
                isManual: true,
              };
            }

            return updatedCell;
          }

          if (
            cell.id === "itAmt" &&
            manualBaseNumeric !== null &&
            Number.isFinite(manualBaseNumeric)
          ) {
            const itPart = Math.max(0, manualBaseNumeric - currentOth);
            return {
              ...cell,
              value: itPart.toFixed(2),
            };
          }

          return cell;
        });

        return {
          ...row,
          data: updatedRowData,
        };
      });
    });

    closeTotalAmountModal(true);
  };

  const openOtherAmountModal = (rowIndex) => {
    const othCell = tableData[rowIndex]?.data?.find((cell) => cell.id === "othAmt");
    if (!othCell) return;

    const currentEntries = othCell.otherAmountEntries || [];
    const currentValue = othCell.value || "";

    const initialEntries =
      currentEntries.length > 0
        ? currentEntries.map((entry) => ({
          ...entry,
          rateOn: entry.rateOn || "",
        }))
        : currentValue
          ? [
            {
              chargeType: "Direct Entry",
              pcs: "",
              ct: "",
              wt: "",
              conPercentage: "",
              rateOn: "",
              rate: "",
              amount: currentValue,
            },
          ]
          : [];

    setOtherAmountModal({
      isOpen: true,
      rowIndex,
      entries: [...initialEntries],
      currentEntry: {
        chargeType: "",
        pcs: "",
        ct: "",
        wt: "",
        conPercentage: "",
        rateOn: "",
        rate: "",
        amount: "",
      },
      editingIndex: undefined,
    });
  };

  const handleSaveOtherAmountEntries = () => {
    const { rowIndex, entries, currentEntry } = otherAmountModal;

    let finalEntries = [...entries];
    if (currentEntry.amount && currentEntry.chargeType) {
      if (isNaN(currentEntry.amount)) {
        alert("Please enter a valid amount");
        return;
      }
      finalEntries.push({ ...currentEntry });
    }

    const totalAmount = finalEntries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0
    );

    const totalLessWeight = finalEntries.reduce((sum, entry) => {
      const lessWeight = calculateLessWeight(entry);
      return sum + (isNaN(lessWeight) ? 0 : lessWeight);
    }, 0);

    setTableData((prev) => {
      const newData = [...prev];
      const row = newData[rowIndex];

      if (!row || !row.data) return prev;

      const grWtCell = row.data.find((cell) => cell.id === "grWt");
      const itRateCell = row.data.find((cell) => cell.id === "itRate");

      const grWt = parseFloat(grWtCell?.value) || 0;

      const othAmtCell = row.data.find((cell) => cell.id === "othAmt");
      if (othAmtCell) {
        othAmtCell.value = formatAmount(totalAmount);
        othAmtCell.otherAmountEntries = finalEntries;
      }

      const lsWtCell = row.data.find((cell) => cell.id === "lsWt");
      if (lsWtCell) {
        lsWtCell.value = formatLessWeight(totalLessWeight);
      }

      const netWeight = calculateNetWeight(grWt, totalLessWeight);
      const netWtCell = row.data.find((cell) => cell.id === "netWt");
      if (netWtCell) {
        netWtCell.value = netWeight.toFixed(2);
      }

      const purityCell = row.data.find((cell) => cell.id === "purity");
      const wastageCell = row.data.find((cell) => cell.id === "wastage");
      const fineWtCell = row.data.find((cell) => cell.id === "fineWt");
      if (netWtCell && purityCell && wastageCell && fineWtCell) {
        const netWt = parseFloat(netWtCell.value) || 0;
        const purity = parseFloat(purityCell.value) || 0;
        const wastage = parseFloat(wastageCell.value) || 0;
        const fineWt = calculateFineWeight(netWt, purity, wastage);
        fineWtCell.value = fineWt.toFixed(2);
      }

      const itAmtCell = row.data.find((cell) => cell.id === "itAmt");
      const itRate = parseFloat(itRateCell?.value) || 0;
      if (itRateCell && itRate && itAmtCell && fineWtCell) {
        const fineWt = parseFloat(fineWtCell.value) || 0;
        const itAmt = (fineWt * itRate).toFixed(2);
        itAmtCell.value = itAmt;
      }

      const itAmt = parseFloat(itAmtCell?.value) || 0;
      const othAmt = parseFloat(othAmtCell?.value) || 0;
      const totalAmt = (itAmt + othAmt).toFixed(2);

      const totalAmtCell = row.data.find((cell) => cell.id === "totalAmt");
      if (totalAmtCell) {
        if (!totalAmtCell.totalAmountDetails?.isManual) {
          totalAmtCell.value = totalAmt;
          if (totalAmtCell.totalAmountDetails) {
            delete totalAmtCell.totalAmountDetails;
          }
        }
      }

      return newData;
    });

    setOtherAmountModal((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  const renderTotalAmountModal = () => {
    if (!totalAmountModal.isOpen) return null;

    return ReactDOM.createPortal(
      <Modal
        show={totalAmountModal.isOpen}
        onHide={() => closeTotalAmountModal(true)}
        centered
        size="sm"
      >
        <Modal.Header closeButton className="border-0 pb-0 align-items-center">
          <Modal.Title className="fw-semibold text-uppercase">
            GST Settlement
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold mb-2">GST (%)</Form.Label>
            <div className="d-flex gap-3 flex-wrap">
              {["3", "5", "12", "18"].map((value) => (
                <Form.Check
                  key={value}
                  type="radio"
                  name="total-amount-gst-selection"
                  id={`total-amount-gst-${value}`}
                  label={`${value}%`}
                  value={value}
                  checked={totalAmountModal.gst === value}
                  onChange={(e) =>
                    setTotalAmountModal((prev) => ({
                      ...prev,
                      gst: e.target.value,
                    }))
                  }
                />
              ))}
            </div>
          </Form.Group>
          <Form.Group controlId="totalAmountModal-amount">
            <div className="d-flex gap-2">
              <Form.Control
                ref={totalAmountInputRef}
                type="text"
                value={totalAmountModal.amount}
                placeholder="Enter price"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^\d*\.?\d*$/.test(value)) {
                    setTotalAmountModal((prev) => ({
                      ...prev,
                      amount: value,
                    }));
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSaveTotalAmount();
                  }
                }}
                style={{ flexBasis: "60%" }}
              />
              <Button
                variant="primary"
                className="px-4"
                onClick={handleSaveTotalAmount}
                style={{ flexBasis: "20%" }}
              >
                Save
              </Button>
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0"></Modal.Footer>
      </Modal>,
      document.getElementById("modal-portal")
    );
  };

  const handleOtherAmountColumnWidthChange = (column, newWidth) => {
    setOtherAmountColumnWidths((prev) => ({
      ...prev,
      [column]: `${newWidth}px`,
    }));
  };
  // Render the otherAmountModal with reduced width
  const renderOtherAmountModal = () => {
    if (!otherAmountModal.isOpen) return null;

    return ReactDOM.createPortal(
      <Modal
        show={otherAmountModal.isOpen}
        onHide={() =>
          setOtherAmountModal((prev) => ({ ...prev, isOpen: false }))
        }
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Other Amount Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            className={`table-responsive border ${buttonTableStyle === "square" ? "rounded-0" : "rounded-1"
              } ${cardClass}`}
          >
            <table
              className={`table text-nowrap mb-0 align-middle table-bordered ${tableClass} theme-header-table`}
            >
              <thead className="text-dark fs-4">
                <tr>
                  {[
                    "No.",
                    "Charge Type",
                    "Pcs",
                    "Ct",
                    "Wt",
                    "Con%",
                    "Rate On",
                    "Rate",
                    "Amount",
                  ].map((header, index) => (
                    <th
                      key={header}
                      className="px-1 py-1 text-center"
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: "600",
                        whiteSpace: "nowrap",
                        border: "1px solid #dee2e6",
                      }}
                    >
                      {header}
                      <div
                        className="resize-handle"
                        style={{
                          position: "absolute",
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: "5px",
                          cursor: "col-resize",
                          backgroundColor: "transparent",
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const startX = e.clientX;
                          const startWidth =
                            e.currentTarget.parentElement.offsetWidth;
                          const column = header
                            .toLowerCase()
                            .replace(/[^a-z]/g, "");

                          const handleMouseMove = (e) => {
                            const diff = e.clientX - startX;
                            const newWidth = Math.max(50, startWidth + diff);
                            handleOtherAmountColumnWidthChange(
                              column,
                              newWidth
                            );
                          };

                          const handleMouseUp = () => {
                            document.removeEventListener(
                              "mousemove",
                              handleMouseMove
                            );
                            document.removeEventListener(
                              "mouseup",
                              handleMouseUp
                            );
                          };

                          document.addEventListener(
                            "mousemove",
                            handleMouseMove
                          );
                          document.addEventListener("mouseup", handleMouseUp);
                        }}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {otherAmountModal.entries.map((entry, index) => (
                  <tr key={index} style={{ backgroundColor: "#ffffff" }}>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: "38px" }}
                      >
                        <span style={{ fontSize: "0.85rem" }}>{index + 1}</span>
                      </div>
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <InlineAutoCompleteInput
                        id={`entry-${index}`} // Add unique ID for existing entries
                        value={entry.chargeType}
                        suggestion={
                          suggestions.length > 0 ? suggestions[0] : ""
                        }
                        onChange={(e) => {
                          const newValue = e.target.value;
                          const newEntries = [...otherAmountModal.entries];
                          newEntries[index].chargeType = newValue;
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            entries: newEntries,
                          }));
                          getSuggestions(newValue);
                        }}
                        onComplete={(suggestion) => {
                          const newEntries = [...otherAmountModal.entries];
                          newEntries[index].chargeType = suggestion;
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            entries: newEntries,
                          }));
                          setSuggestions([]);
                        }}
                        className="form-control border-0 rounded-0"
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                        }}
                        onFocus={(e) => {
                          e.target.style.backgroundColor = "#fff";
                          if (entry.chargeType) {
                            getSuggestions(entry.chargeType);
                          }
                        }}
                        onBlur={(e) => {
                          e.target.style.backgroundColor = "transparent";
                          setTimeout(() => setSuggestions([]), 200);
                        }}
                      />
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <input
                        type="text"
                        value={entry.pcs}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*$/.test(value)) {
                            const newEntries = [...otherAmountModal.entries];
                            const updatedEntry = {
                              ...newEntries[index],
                              pcs: value,
                            };
                            // Recalculate amount based on Rate On
                            if (updatedEntry.rate && updatedEntry.rateOn) {
                              updatedEntry.amount = formatAmount(
                                calculateAmount(updatedEntry)
                              );
                            }
                            newEntries[index] = updatedEntry;
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }
                        }}
                        className="form-control border-0 rounded-0"
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                        onFocus={(e) =>
                          (e.target.style.backgroundColor = "#fff")
                        }
                        onBlur={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      />
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <input
                        type="text"
                        value={entry.ct}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            const newEntries = [...otherAmountModal.entries];
                            const updatedEntry = {
                              ...newEntries[index],
                              ct: value,
                            };
                            // Recalculate amount based on Rate On
                            if (updatedEntry.rate && updatedEntry.rateOn) {
                              updatedEntry.amount = formatAmount(
                                calculateAmount(updatedEntry)
                              );
                            }
                            newEntries[index] = updatedEntry;
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }
                        }}
                        className="form-control border-0 rounded-0"
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                        onFocus={(e) =>
                          (e.target.style.backgroundColor = "#fff")
                        }
                        onBlur={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      />
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <input
                        type="text"
                        value={entry.wt}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            const newEntries = [...otherAmountModal.entries];
                            const updatedEntry = {
                              ...newEntries[index],
                              wt: value,
                            };
                            // Recalculate amount based on Rate On
                            if (updatedEntry.rate && updatedEntry.rateOn) {
                              updatedEntry.amount = formatAmount(
                                calculateAmount(updatedEntry)
                              );
                            }
                            newEntries[index] = updatedEntry;
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }
                        }}
                        className="form-control border-0 rounded-0"
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                        onFocus={(e) =>
                          (e.target.style.backgroundColor = "#fff")
                        }
                        onBlur={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      />
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <input
                        type="text"
                        value={entry.conPercentage}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            const newEntries = [...otherAmountModal.entries];
                            newEntries[index] = {
                              ...newEntries[index],
                              conPercentage: value,
                            };
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }
                        }}
                        className="form-control border-0 rounded-0"
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                        onFocus={(e) =>
                          (e.target.style.backgroundColor = "#fff")
                        }
                        onBlur={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      />
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <div className="position-relative">
                        <input
                          type="text"
                          value={entry.rateOn}
                          onChange={(e) => {
                            const newEntries = [...otherAmountModal.entries];
                            newEntries[index].rateOn = e.target.value;
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }}
                          className="form-control border-0 rounded-0"
                          style={{
                            fontSize: "0.85rem",
                            padding: "0.4rem 0.5rem",
                            backgroundColor: "transparent",
                            width: "100%",
                            transition: "background-color 0.2s",
                          }}
                          onFocus={(e) => {
                            e.target.style.backgroundColor = "#fff";
                            const rect = e.target.getBoundingClientRect();
                            const dropdown = e.target.nextElementSibling;
                            if (dropdown) {
                              // Calculate max width based on content
                              const maxWidth = Math.max(
                                ...rateOnOptions.map((item) => {
                                  const tempSpan =
                                    document.createElement("span");
                                  tempSpan.style.fontSize = "0.85rem";
                                  tempSpan.style.visibility = "hidden";
                                  tempSpan.style.position = "absolute";
                                  tempSpan.style.whiteSpace = "nowrap";
                                  tempSpan.textContent = item;
                                  document.body.appendChild(tempSpan);
                                  const width = tempSpan.offsetWidth;
                                  document.body.removeChild(tempSpan);
                                  return width;
                                }),
                                rect.width // minimum width is input width
                              );

                              dropdown.style.display = "block";
                              dropdown.style.position = "fixed";
                              dropdown.style.top = `${rect.bottom}px`;
                              dropdown.style.left = `${rect.left}px`;
                              dropdown.style.width = "auto";
                              dropdown.style.minWidth = `${rect.width}px`;
                              dropdown.style.maxWidth = `${maxWidth + 32}px`; // Add padding
                              dropdown.style.zIndex = "9999";
                              // Reset highlighted index when opening dropdown
                              setHighlightedIndex(0);
                            }
                          }}
                          onBlur={(e) => {
                            e.target.style.backgroundColor = "transparent";
                            setTimeout(() => {
                              const dropdown = e.target.nextElementSibling;
                              if (dropdown) {
                                dropdown.style.display = "none";
                              }
                            }, 200);
                          }}
                        />
                        <div
                          className="dropdown-menu shadow-sm"
                          style={{
                            display: "none",
                            maxHeight: "200px",
                            overflowY: "auto",
                            backgroundColor: "#fff",
                            position: "fixed",
                            zIndex: 9999,
                            border: "1px solid rgba(0,0,0,.15)",
                            borderRadius: "0.25rem",
                            padding: "0.5rem 0",
                            width: "auto",
                            minWidth: "100%",
                          }}
                        >
                          {rateOnOptions
                            .filter((option) =>
                              option
                                .toLowerCase()
                                .includes((entry.rateOn || "").toLowerCase())
                            )
                            .map((option, i) => (
                              <div
                                key={i}
                                className={`dropdown-item ${i === highlightedIndex ? "active" : ""
                                  }`}
                                style={{
                                  cursor: "pointer",
                                  padding: "0.25rem 1rem",
                                  fontSize: "0.85rem",
                                  backgroundColor:
                                    i === highlightedIndex
                                      ? "var(--theme-primary)"
                                      : option.toLowerCase() ===
                                        entry.rateOn.toLowerCase()
                                        ? "#f0f0f0"
                                        : "transparent",
                                  color:
                                    i === highlightedIndex ? "#fff" : "inherit",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                onMouseEnter={() => setHighlightedIndex(i)}
                                onMouseDown={() => {
                                  const newEntries = [
                                    ...otherAmountModal.entries,
                                  ];
                                  newEntries[index].rateOn = option;
                                  setOtherAmountModal((prev) => ({
                                    ...prev,
                                    entries: newEntries,
                                  }));
                                  // Hide dropdown after selection
                                  const dropdown = document.querySelector(
                                    '.dropdown-menu[style*="display: block"]'
                                  );
                                  if (dropdown) {
                                    dropdown.style.display = "none";
                                  }
                                }}
                              >
                                {option}
                              </div>
                            ))}
                        </div>
                      </div>
                    </td>
                    <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                      <input
                        type="text"
                        value={entry.rate}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            const newEntries = [...otherAmountModal.entries];
                            const updatedEntry = {
                              ...newEntries[index],
                              rate: value,
                            };
                            // Calculate amount whenever rate changes
                            if (value && updatedEntry.rateOn) {
                              updatedEntry.amount = formatAmount(
                                calculateAmount(updatedEntry)
                              );
                            } else {
                              updatedEntry.amount = "";
                            }
                            newEntries[index] = updatedEntry;
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }
                        }}
                        className="form-control border-0 rounded-0"
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                        onFocus={(e) =>
                          (e.target.style.backgroundColor = "#fff")
                        }
                        onBlur={(e) =>
                          (e.target.style.backgroundColor = "transparent")
                        }
                      />
                    </td>
                    <td
                      className="p-0 position-relative"
                      style={{ border: "1px solid #dee2e6" }}
                    >
                      <input
                        type="text"
                        value={entry.amount}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            const newEntries = [...otherAmountModal.entries];
                            newEntries[index].amount = value;
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              entries: newEntries,
                            }));
                          }
                        }}
                        onFocus={(e) => {
                          e.target.style.backgroundColor = "#fff";
                        }}
                        onBlur={(e) => {
                          e.target.style.backgroundColor = "transparent";
                        }}
                        className="form-control border-0 rounded-0"
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                          textAlign: "left",
                        }}
                      />
                    </td>
                  </tr>
                ))}
                {/* Empty row for new entries */}
                <tr style={{ backgroundColor: "#ffffff" }}>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{ height: "38px" }}
                    >
                      <span style={{ fontSize: "0.85rem" }}>
                        {otherAmountModal.entries.length + 1}
                      </span>
                    </div>
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <InlineAutoCompleteInput
                      id="current-entry" // Add unique ID for current entry
                      value={otherAmountModal.currentEntry.chargeType}
                      suggestion={suggestions.length > 0 ? suggestions[0] : ""}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setOtherAmountModal((prev) => ({
                          ...prev,
                          currentEntry: {
                            ...prev.currentEntry,
                            chargeType: newValue,
                          },
                        }));
                        getSuggestions(newValue);
                      }}
                      onComplete={(suggestion) => {
                        setOtherAmountModal((prev) => ({
                          ...prev,
                          currentEntry: {
                            ...prev.currentEntry,
                            chargeType: suggestion,
                          },
                        }));
                        setSuggestions([]);
                      }}
                      className="form-control border-0 rounded-0"
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                      }}
                      onFocus={(e) => {
                        e.target.style.backgroundColor = "#fff";
                        if (otherAmountModal.currentEntry.chargeType) {
                          getSuggestions(
                            otherAmountModal.currentEntry.chargeType
                          );
                        }
                      }}
                      onBlur={(e) => {
                        e.target.style.backgroundColor = "transparent";
                        setTimeout(() => setSuggestions([]), 200);
                      }}
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.pcs}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*$/.test(value)) {
                          setOtherAmountModal((prev) => {
                            const updatedEntry = {
                              ...prev.currentEntry,
                              pcs: value,
                            };
                            // Recalculate amount if rate on is "per pcs"
                            if (
                              updatedEntry.rateOn?.toLowerCase() ===
                              "per pcs" &&
                              updatedEntry.rate
                            ) {
                              updatedEntry.amount =
                                calculateAmount(updatedEntry).toFixed(2);
                            }
                            return {
                              ...prev,
                              currentEntry: updatedEntry,
                            };
                          });
                        }
                      }}
                      className="form-control border-0 rounded-0"
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) =>
                        (e.target.style.backgroundColor = "transparent")
                      }
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.ct}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setOtherAmountModal((prev) => {
                            const updatedEntry = {
                              ...prev.currentEntry,
                              ct: value,
                            };
                            // Recalculate amount if rate on is "per ct"
                            if (
                              updatedEntry.rateOn?.toLowerCase() === "per ct" &&
                              updatedEntry.rate
                            ) {
                              updatedEntry.amount =
                                calculateAmount(updatedEntry).toFixed(2);
                            }
                            return {
                              ...prev,
                              currentEntry: updatedEntry,
                            };
                          });
                        }
                      }}
                      className="form-control border-0 rounded-0"
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) =>
                        (e.target.style.backgroundColor = "transparent")
                      }
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.wt}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setOtherAmountModal((prev) => {
                            const updatedEntry = {
                              ...prev.currentEntry,
                              wt: value,
                            };
                            // Recalculate amount if rate on is "per wt"
                            if (
                              updatedEntry.rateOn?.toLowerCase() === "per wt" &&
                              updatedEntry.rate
                            ) {
                              updatedEntry.amount =
                                calculateAmount(updatedEntry).toFixed(2);
                            }
                            return {
                              ...prev,
                              currentEntry: updatedEntry,
                            };
                          });
                        }
                      }}
                      className="form-control border-0 rounded-0"
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) =>
                        (e.target.style.backgroundColor = "transparent")
                      }
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.conPercentage}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            currentEntry: {
                              ...prev.currentEntry,
                              conPercentage: value,
                            },
                          }));
                        }
                      }}
                      className="form-control border-0 rounded-0"
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) =>
                        (e.target.style.backgroundColor = "transparent")
                      }
                    />
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <div className="position-relative">
                      <input
                        type="text"
                        value={otherAmountModal.currentEntry.rateOn}
                        onChange={(e) => {
                          handleRateOnDropdown(null, e.target.value, true);
                          const dropdown = e.target.nextElementSibling;
                          if (dropdown) {
                            dropdown.style.display = "block";
                          }
                        }}
                        onKeyDown={(e) => handleRateOnKeyDown(e, null, true)}
                        className="form-control border-0 rounded-0"
                        style={{
                          fontSize: "0.85rem",
                          padding: "0.4rem 0.5rem",
                          backgroundColor: "transparent",
                          width: "100%",
                          transition: "background-color 0.2s",
                        }}
                        onFocus={(e) => {
                          e.target.style.backgroundColor = "#fff";
                          const rect = e.target.getBoundingClientRect();
                          const dropdown = e.target.nextElementSibling;
                          if (dropdown) {
                            // Calculate max width based on content
                            const maxWidth = Math.max(
                              ...rateOnOptions.map((item) => {
                                const tempSpan = document.createElement("span");
                                tempSpan.style.fontSize = "0.85rem";
                                tempSpan.style.visibility = "hidden";
                                tempSpan.style.position = "absolute";
                                tempSpan.style.whiteSpace = "nowrap";
                                tempSpan.textContent = item;
                                document.body.appendChild(tempSpan);
                                const width = tempSpan.offsetWidth;
                                document.body.removeChild(tempSpan);
                                return width;
                              }),
                              rect.width // minimum width is input width
                            );

                            dropdown.style.display = "block";
                            dropdown.style.position = "fixed";
                            dropdown.style.top = `${rect.bottom}px`;
                            dropdown.style.left = `${rect.left}px`;
                            dropdown.style.width = "auto";
                            dropdown.style.minWidth = `${rect.width}px`;
                            dropdown.style.maxWidth = `${maxWidth + 32}px`; // Add padding
                            dropdown.style.zIndex = "9999";
                            // Reset highlighted index when opening dropdown
                            setHighlightedIndex(0);
                          }
                        }}
                        onBlur={(e) => {
                          e.target.style.backgroundColor = "transparent";
                          setTimeout(() => {
                            const dropdown = e.target.nextElementSibling;
                            if (dropdown) {
                              dropdown.style.display = "none";
                            }
                          }, 200);
                        }}
                      />
                      <div
                        className="dropdown-menu shadow-sm"
                        style={{
                          display: "none",
                          maxHeight: "200px",
                          overflowY: "auto",
                          backgroundColor: "#fff",
                          position: "fixed",
                          zIndex: 9999,
                          border: "1px solid rgba(0,0,0,.15)",
                          borderRadius: "0.25rem",
                          padding: "0.5rem 0",
                          width: "auto",
                          minWidth: "100%",
                        }}
                      >
                        {rateOnOptions
                          .filter((option) =>
                            option
                              .toLowerCase()
                              .includes(
                                (
                                  otherAmountModal.currentEntry.rateOn || ""
                                ).toLowerCase()
                              )
                          )
                          .map((option, i) => (
                            <div
                              key={i}
                              className={`dropdown-item ${i === highlightedIndex ? "active" : ""
                                }`}
                              style={{
                                cursor: "pointer",
                                padding: "0.25rem 1rem",
                                fontSize: "0.85rem",
                                backgroundColor:
                                  i === highlightedIndex
                                    ? "var(--theme-primary)"
                                    : option.toLowerCase() ===
                                      otherAmountModal.currentEntry.rateOn.toLowerCase()
                                      ? "#f0f0f0"
                                      : "transparent",
                                color:
                                  i === highlightedIndex ? "#fff" : "inherit",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              onMouseEnter={() => setHighlightedIndex(i)}
                              onMouseDown={() => {
                                handleRateOnDropdown(null, option, true);
                                // Hide dropdown after selection
                                const dropdown = document.querySelector(
                                  '.dropdown-menu[style*="display: block"]'
                                );
                                if (dropdown) {
                                  dropdown.style.display = "none";
                                }
                              }}
                            >
                              {option}
                            </div>
                          ))}
                      </div>
                    </div>
                  </td>
                  <td className="p-0" style={{ border: "1px solid #dee2e6" }}>
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.rate}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setOtherAmountModal((prev) => {
                            const updatedEntry = {
                              ...prev.currentEntry,
                              rate: value,
                            };
                            // Calculate amount whenever rate changes
                            if (value && updatedEntry.rateOn) {
                              updatedEntry.amount = formatAmount(
                                calculateAmount(updatedEntry)
                              );
                            } else {
                              updatedEntry.amount = "";
                            }
                            return {
                              ...prev,
                              currentEntry: updatedEntry,
                            };
                          });
                        }
                      }}
                      className="form-control border-0 rounded-0"
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                      onFocus={(e) => (e.target.style.backgroundColor = "#fff")}
                      onBlur={(e) =>
                        (e.target.style.backgroundColor = "transparent")
                      }
                    />
                  </td>
                  <td
                    className="p-0 position-relative"
                    style={{ border: "1px solid #dee2e6" }}
                  >
                    <input
                      type="text"
                      value={otherAmountModal.currentEntry.amount}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            currentEntry: {
                              ...prev.currentEntry,
                              amount: value,
                            },
                          }));
                        }
                      }}
                      onKeyDown={(e) => {
                        // Save and close modal on Enter if amount is valid
                        if (e.key === "Enter") {
                          const value = otherAmountModal.currentEntry.amount;
                          if (value && !isNaN(value) && Number(value) > 0) {
                            e.preventDefault();

                            // Add current entry to entries
                            const finalEntries = [
                              ...otherAmountModal.entries,
                              otherAmountModal.currentEntry,
                            ];

                            // Calculate total amount
                            const totalAmount = finalEntries.reduce(
                              (sum, entry) => sum + Number(entry.amount || 0),
                              0
                            );

                            // Calculate total less weight
                            const totalLessWeight = finalEntries.reduce(
                              (sum, entry) => {
                                const lessWeight = calculateLessWeight(entry);
                                return (
                                  sum + (isNaN(lessWeight) ? 0 : lessWeight)
                                );
                              },
                              0
                            );

                            // Update table data with both values
                            setTableData((prev) => {
                              const newData = [...prev];
                              const row = newData[otherAmountModal.rowIndex];

                              if (!row || !row.data) return prev;

                              // Update Other Amount (rounded)
                              const othAmtCell = row.data.find(
                                (cell) => cell.id === "othAmt"
                              );
                              if (othAmtCell) {
                                othAmtCell.value = formatAmount(totalAmount);
                                othAmtCell.otherAmountEntries = finalEntries;
                              }

                              // Update Less Weight (always show with 2 decimal places)
                              const lsWtCell = row.data.find(
                                (cell) => cell.id === "lsWt"
                              );
                              if (lsWtCell) {
                                if (totalLessWeight > 0) {
                                  lsWtCell.value =
                                    formatLessWeight(totalLessWeight);
                                }
                              }

                              return newData;
                            });

                            // Close modal
                            setOtherAmountModal((prev) => ({
                              ...prev,
                              isOpen: false,
                            }));
                          }
                        }
                      }}
                      onFocus={(e) => {
                        e.target.style.backgroundColor = "#fff";
                        // When focus reaches the amount field, add a new row in the background
                        setTimeout(() => {
                          setOtherAmountModal((prev) => {
                            // Only add new row if current row has any data
                            if (
                              prev.currentEntry.chargeType ||
                              prev.currentEntry.pcs ||
                              prev.currentEntry.ct ||
                              prev.currentEntry.wt ||
                              prev.currentEntry.conPercentage ||
                              prev.currentEntry.rateOn ||
                              prev.currentEntry.rate
                            ) {
                              return {
                                ...prev,
                                entries: [...prev.entries],
                                currentEntry: { ...prev.currentEntry },
                              };
                            }
                            return prev;
                          });
                        }, 0);
                      }}
                      onBlur={(e) => {
                        e.target.style.backgroundColor = "transparent";
                        // Move to next row on blur if amount is valid
                        const value = otherAmountModal.currentEntry.amount;
                        if (value && !isNaN(value) && Number(value) > 0) {
                          // Add current entry and move to next row
                          setOtherAmountModal((prev) => ({
                            ...prev,
                            entries: [
                              ...prev.entries,
                              { ...prev.currentEntry },
                            ],
                            currentEntry: {
                              chargeType: "",
                              pcs: "",
                              ct: "",
                              wt: "",
                              conPercentage: "",
                              rateOn: "",
                              rate: "",
                              amount: "",
                            },
                          }));
                          // Focus on first cell of next row
                          setTimeout(() => {
                            const firstInput = document.querySelector(
                              ".modal-body tbody tr:last-child td:nth-child(2) input"
                            );
                            if (firstInput) {
                              firstInput.focus();
                            }
                          }, 0);
                        }
                      }}
                      className="form-control border-0 rounded-0"
                      style={{
                        fontSize: "0.85rem",
                        padding: "0.4rem 0.5rem",
                        backgroundColor: "transparent",
                        width: "100%",
                        transition: "background-color 0.2s",
                        textAlign: "left",
                      }}
                    />
                  </td>
                </tr>
              </tbody>
              <tfoot>{/* Remove the total row from table */}</tfoot>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-4">
            <div className="d-flex align-items-center gap-2">
              <span className="text-dark" style={{ fontSize: "0.9rem" }}>
                LWt:
              </span>
              <span className="fw-bold" style={{ fontSize: "0.9rem" }}>
                {formatLessWeight(
                  otherAmountModal.entries.reduce((sum, entry) => {
                    const lessWeight = calculateLessWeight(entry);
                    return sum + (isNaN(lessWeight) ? 0 : lessWeight);
                  }, 0)
                )}
              </span>
            </div>
            <div
              className="border-end h-100 mx-2"
              style={{ width: "1px", backgroundColor: "#dee2e6" }}
            ></div>
            <div className="d-flex align-items-center gap-2">
              <span className="text-dark" style={{ fontSize: "0.9rem" }}>
                Total:
              </span>
              <span className="fw-bold" style={{ fontSize: "0.9rem" }}>
                ₹{" "}
                {formatAmount(
                  otherAmountModal.entries.reduce(
                    (sum, entry) => sum + Number(entry.amount || 0),
                    0
                  )
                )}
              </span>
            </div>
          </div>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={handleSaveOtherAmountEntries}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>,
      document.getElementById("modal-portal")
    );
  };
  // Update the Rate On dropdown implementation
  const handleRateOnDropdown = (rowIndex, value, isCurrentEntry = false) => {
    if (isCurrentEntry) {
      setOtherAmountModal((prev) => {
        const updatedEntry = {
          ...prev.currentEntry,
          rateOn: value,
        };
        // Calculate amount when rate on changes
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        return {
          ...prev,
          currentEntry: updatedEntry,
        };
      });
    } else {
      setOtherAmountModal((prev) => {
        const newEntries = [...prev.entries];
        const updatedEntry = {
          ...newEntries[rowIndex],
          rateOn: value,
        };
        // Calculate amount when rate on changes
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        newEntries[rowIndex] = updatedEntry;
        return {
          ...prev,
          entries: newEntries,
        };
      });
    }

    // Hide the dropdown after selection
    const dropdowns = document.querySelectorAll(".dropdown-menu");
    dropdowns.forEach((dropdown) => {
      if (dropdown.style.display === "block") {
        dropdown.style.display = "none";
      }
    });
  };

  // Add keyboard navigation for Rate On dropdown
  const handleRateOnKeyDown = (e, rowIndex, isCurrentEntry = false) => {
    const currentValue = isCurrentEntry
      ? otherAmountModal.currentEntry.rateOn
      : otherAmountModal.entries[rowIndex].rateOn;

    const filtered = rateOnOptions.filter((option) =>
      option.toLowerCase().includes((currentValue || "").toLowerCase())
    );

    if (filtered.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filtered[highlightedIndex]) {
          handleRateOnDropdown(
            rowIndex,
            filtered[highlightedIndex],
            isCurrentEntry
          );
        }
        break;
      case "Escape":
        e.preventDefault();
        // Close dropdown
        const dropdown = e.target.nextElementSibling;
        if (dropdown) {
          dropdown.style.display = "none";
        }
        break;
      default:
        break;
    }
  };


  // Add this function right before the return statement in the Table component
  // Function to position tooltip based on screen size
  const positionTooltip = (targetElement, tooltip) => {
    const rect = targetElement.getBoundingClientRect();
    tooltip.style.display = "block";

    // Get arrow element
    const arrow = tooltip.querySelector(".tooltip-arrow");

    // Responsive positioning based on viewport
    const viewportWidth = window.innerWidth;

    if (viewportWidth < 576) {
      // Small screens (Bootstrap's sm breakpoint)
      // Position directly below the "i" button
      tooltip.style.left = `${rect.left + rect.width / 2 - 100}px`; // Center horizontally (tooltip width is 200px)
      tooltip.style.top = `${rect.bottom + 10}px`; // Position below the button
      tooltip.style.maxWidth = "calc(100vw - 20px)"; // 10px padding on each side

      // Position arrow at top center
      if (arrow) {
        arrow.style.top = "-6px"; // Adjusted for better alignment
        arrow.style.left = "50%";
        arrow.style.transform = "translateX(-50%) rotate(45deg)";
        arrow.style.borderRight = "none";
        arrow.style.borderBottom = "none";
        arrow.style.borderLeft = "1px solid rgba(0,0,0,0.1)";
        arrow.style.borderTop = "1px solid rgba(0,0,0,0.1)";
      }
    } else {
      // Position to the right on larger screens
      tooltip.style.left = `${rect.right + 10}px`;
      tooltip.style.top = `${rect.top - 5}px`;
      tooltip.style.maxWidth = "300px";

      // Position arrow at left middle
      if (arrow) {
        arrow.style.top = "15px";
        arrow.style.left = "-6px";
        arrow.style.transform = "rotate(45deg)";
        arrow.style.borderRight = "none";
        arrow.style.borderBottom = "none";
      }
    }
  };

  // Add calculation functions before the Table component
  const calculateTotalCt = (entry) => {
    const ct = parseFloat(entry.ct);

    if (isNaN(ct)) return 0;
    return ct;
  };

  const calculateTotalWt = (entry) => {
    const pcs = parseFloat(entry.pcs);
    const wt = parseFloat(entry.wt);

    if (isNaN(pcs) || isNaN(wt)) return 0;
    return pcs * wt;
  };

  const calculateLessWeight = (entry) => {
    // Return the weight value directly (same as Wt field)
    const wt = parseFloat(entry.wt);
    if (isNaN(wt)) return 0;

    // Get concentration percentage
    const conPercentage = parseFloat(entry.conPercentage);

    // If there's a valid concentration percentage, calculate less weight based on that
    if (!isNaN(conPercentage) && conPercentage > 0) {
      return wt * (conPercentage / 100);
    }

    // If no concentration percentage, return the weight value directly
    return wt;
  };

  const calculateAmount = (entry) => {
    if (!entry.rate || isNaN(entry.rate)) return 0;

    const rate = parseFloat(entry.rate);
    const rateOn = entry.rateOn?.toLowerCase() || "";
    let amount = 0;

    switch (rateOn) {
      case "fixed":
        amount = rate;
        break;
      case "pcs":
        amount =
          entry.pcs && !isNaN(entry.pcs) ? rate * parseFloat(entry.pcs) : 0;
        break;
      case "ct":
        const totalCt = calculateTotalCt(entry);
        amount = totalCt ? rate * totalCt : 0;
        break;
      case "wt":
        const totalWt = calculateTotalWt(entry);
        amount = totalWt ? rate * totalWt : 0;
        break;
      default:
        amount = 0;
    }

    // Return the formatted amount
    return amount;
  };

  const getRowEffectiveAmount = (row) => {
    if (!row || !row.data) return 0;

    const totalAmtCell = row.data.find((cell) => cell.id === "totalAmt");
    const totalAmtValue = totalAmtCell ? parseFloat(totalAmtCell.value) : NaN;

    return isNaN(totalAmtValue) ? 0 : totalAmtValue;
  };

  const calculateTotal = () => {
    if (!tableData) return 0;
    return tableData.reduce((sum, row) => {
      const effectiveAmount = getRowEffectiveAmount(row);
      return sum + effectiveAmount;
    }, 0);
  };

  // Add a helper function for number formatting
  const formatAmount = (value) => {
    if (!value || isNaN(value)) return "";
    // Convert to number and handle rounding based on decimal part
    const num = parseFloat(value);
    const decimalPart = num - Math.floor(num);
    // If decimal part is >= 0.5, round up, otherwise round down
    return decimalPart >= 0.5
      ? Math.ceil(num).toString()
      : Math.floor(num).toString();
  };

  // Update formatLessWeight to always return a value with 2 decimal places
  const formatLessWeight = (value) => {
    // Convert to number first
    const num = parseFloat(value);
    // Check if it's a valid number (including 0)
    if (isNaN(num)) return "0.00";
    // Always return with 2 decimal places
    return num.toFixed(2);
  };

  // Calculate total discount amount based on discount type and value
  const calculateDiscountAmount = () => {
    const totalAmount = calculateTotal();
    if (!discountValue || isNaN(discountValue) || totalAmount <= 0) {
      return 0;
    }
    const discount = parseFloat(discountValue);
    if (discountType === "%") {
      return (totalAmount * discount) / 100;
    } else {
      return discount;
    }
  };

  // Calculate GST amount based on percentage
  const calculateGstAmount = () => {
    const totalAmount = calculateTotal();
    const discountAmt = calculateDiscountAmount();
    const taxableAmount = totalAmount - discountAmt;

    if (!gstPercentage || isNaN(gstPercentage)) {
      return {
        total: 0,
        sgst: 0,
        cgst: 0,
        igst: 0,
      };
    }

    const gstRate = parseFloat(gstPercentage);
    const totalGstAmount = (taxableAmount * gstRate) / 100;

    // Get the business state (you may need to replace this with your actual business state)
    const businessState = "Gujarat"; // Replace with your actual business state

    // Check if customer state matches business state (case-insensitive comparison)
    const isIntraStateTransaction =
      customerDetails.state &&
      customerDetails.state.toLowerCase() === businessState.toLowerCase();

    if (isIntraStateTransaction) {
      // For intra-state transactions, split GST into SGST and CGST
      // SGST = (Taxable Amount × X%) ÷ 2
      // CGST = (Taxable Amount × X%) ÷ 2
      const halfGstRate = gstRate / 2;
      const sgstAmount = (taxableAmount * halfGstRate) / 100;
      const cgstAmount = (taxableAmount * halfGstRate) / 100;

      // console.log("Intra-state transaction detected. Splitting GST:");
      // console.log("SGST Amount:", sgstAmount);
      // console.log("CGST Amount:", cgstAmount);

      return {
        total: totalGstAmount,
        sgst: sgstAmount,
        cgst: cgstAmount,
        igst: 0,
      };
    } else {
      // console.log(
      //   "Inter-state transaction detected. Using IGST:",
      //   totalGstAmount
      // );
      // For inter-state transactions, apply full GST as IGST
      return {
        total: totalGstAmount,
        sgst: 0,
        cgst: 0,
        igst: totalGstAmount,
      };
    }
  };

  // Amount before payment-section round-off (final - discount + GST).
  const calculateAmountBeforeRoundOff = () => {
    const totalAmount = calculateTotal();
    const discountAmt = calculateDiscountAmount();
    const gstAmt = calculateGstAmount().total;
    return totalAmount - discountAmt + gstAmt;
  };

  const getAutoPaymentRoundOff = () =>
    autoRoundOff(calculateAmountBeforeRoundOff()).roundOff;

  const getEffectiveRoundOff = () => {
    if (isManualRoundOff) {
      return parseFloat(roundOffAmount) || 0;
    }
    return getAutoPaymentRoundOff();
  };

  const getDisplayRoundOff = () => {
    if (isManualRoundOff) {
      return roundOffAmount;
    }
    return getAutoPaymentRoundOff().toFixed(2);
  };

  // Calculate grand total (total - discount + GST + round-off adjustment)
  const calculateGrandTotal = () => {
    const amountBeforeRoundOff = calculateAmountBeforeRoundOff();
    const roundOff = getEffectiveRoundOff();
    return (amountBeforeRoundOff + roundOff).toFixed(2);
  };

  const calculatePurchaseRemaining = () => {
    const grand = parseFloat(calculateGrandTotal()) || 0;
    const paid = purchasePaymentRows.reduce(
      (sum, r) => sum + (parseFloat(r.amount) || 0),
      0,
    );
    return Math.max(0, grand - paid).toFixed(2);
  };


  const calculatePurchaseTotalPaid = () => {
    return purchasePaymentRows.reduce(
      (sum, r) => sum + (parseFloat(r.amount) || 0),
      0,
    );
  };

  const calculatePurchasePayableAmount = () => {
    const totalPaid = calculatePurchaseTotalPaid();
    const grandTotal = parseFloat(calculateGrandTotal()) || 0;
    if (totalPaid > grandTotal) {
      return (totalPaid - grandTotal).toFixed(2);
    }
    return "0.00";
  };


  // design / size removed from Purchase rows
  const [pcs, setPcs] = useState(0); // Initialize pcs state
  const [grWt, setGrWt] = useState(0); // Initialize grWt state
  const [lsWt, setLsWt] = useState(0); // Initialize lsWt state
  const [netWt, setNetWt] = useState(0); // Initialize netWt state
  const [No, setNo] = useState(0); // Initialize No state (assuming it's a number)
  const [showSuccessModal, setShowSuccessModal] = useState(false); // State for modal visibility
  const [discountType, setDiscountType] = useState("%"); // Discount type (% or ₹)
  const [discountValue, setDiscountValue] = useState(""); // Discount value
  const [roundOffAmount, setRoundOffAmount] = useState(""); // Change to string for input
  const [isManualRoundOff, setIsManualRoundOff] = useState(false);
  const [note, setNote] = useState(""); // Add note state

  // After-save navigation choice modal (Stock vs Purchase Table)
  const [showPostSaveModal, setShowPostSaveModal] = useState(false);

  // Add resetForm function before handleSubmit
  const resetForm = () => {
    setBillDate(new Date().toISOString().split("T")[0]);

    // Reset customer fields
    setCustomerMobile("");
    setCustomerMobileSearchText("");
    setCustomerName("");
    setCustomerNameSearchText("");
    setCustomerAddress("");
    setCustomerCity("");
    setSelectedCustomerId(null);
    setCustomerDetails({
      email: "",
      birthDate: "",
      anniversaryDate: "",
      companyName: "",
      companyAddress: "",
      gstNumber: "",
      aadharNo: "",
      panNo: "",
      maritalStatus: "",
      // openingBalance: "",
      // pendingBalance: "",
      balance: "",
      saleBalance: "",
      reference: "",
      state: "",
    });

    // Reset table data
    setTableData([{ id: 1, data: initialRowData }]);
    setItemFieldErrors({});

    // Reset other fields
    setDiscountType("%");
    setDiscountValue("");
    setGstPercentage("3");
    setRoundOffAmount("");
    setIsManualRoundOff(false);
    setNote("");
    setFollowUpDate("");
    setPurchasePaymentRows(defaultPurchasePaymentRows());
  };

  const handlePostSaveChoice = (goToStock) => {
    setShowPostSaveModal(false);
    if (goToStock) {
      navigate("/stocktable");
    } else {
      navigate("/purchasetable");
    }
  };

  // Keep idempotency_key stable across retries of the same submit attempt.
  const purchaseCreateIdempotencyKeyRef = useRef(null);
  const purchaseCreatePayloadFingerprintRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const itemErrors = collectLineItemFieldErrors(
        tableData,
        PURCHASE_RATE_CUT_LINE_ERROR_OPTIONS,
      );
      const hasItemErrors = Object.keys(itemErrors).length > 0;
      setItemFieldErrors(hasItemErrors ? itemErrors : {});

      if (!partyId) {
        toast.error("Please select a supplier (Name)");
        return;
      }

      // Format dates
      const formattedDate = billDate
        ? new Date(billDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];
      const formattedFollowupDate = followUpDate
        ? new Date(followUpDate).toISOString().split("T")[0]
        : null;

      if (hasItemErrors) {
        return;
      }

      // Process ratecut metal items
      const settleItems = [];
      for (let rowIndex = 0; rowIndex < tableData.length; rowIndex++) {
        const rowData = tableData[rowIndex].data.reduce((acc, cell) => {
          acc[cell.id] = cell.value;
          if (cell.id === "tagNo" && cell.productId != null) {
            acc.productId = cell.productId;
          }
          if (cell.id === "productGroup" && cell.productGroupId != null) {
            acc.productGroupId = cell.productGroupId;
          }
          if (cell.id === "totalAmt" && cell.totalAmountDetails?.gst) {
            acc.totalAmtGst = cell.totalAmountDetails.gst;
          }
          return acc;
        }, {});

        // Skip empty rows
        if (!String(rowData.productGroup || "").trim()) {
          continue;
        }

        const tagNoTrimmed = String(rowData.tagNo || "").trim();
        const hasTag = Boolean(tagNoTrimmed);

        const selectedProductGroup =
          rowData.productGroupId != null
            ? dropdownData.productGroup.find(
              (g) => String(g.value) === String(rowData.productGroupId),
            ) || findProductGroupMatch(rowData.productGroup)
            : findProductGroupMatch(rowData.productGroup);
        if (!selectedProductGroup) {
          throw new Error(`Invalid product group in row ${rowIndex + 1}`);
        }

        let productId = null;
        if (hasTag) {
          productId = rowData.productId;
          if (!productId) {
            const productRes = await showProducts({ tag_no: tagNoTrimmed });
            const products = normalizeProductStockShowResponse(productRes);
            const product = products[0];
            productId =
              product?.id ?? product?.product_id ?? product?.product?.id ?? null;
          }
          if (!productId) {
            throw new Error(
              `Unable to resolve product for tag in row ${rowIndex + 1}`,
            );
          }
        }

        const gwt = Number(rowData.grWt) || 0;
        const nwt = Number(rowData.netWt) || 0;
        const fine_wt = Number(rowData.fineWt) || 0;
        const hasAnyWeight =
          (Number.isFinite(gwt) && gwt > 0) ||
          (Number.isFinite(nwt) && nwt > 0) ||
          (Number.isFinite(fine_wt) && fine_wt > 0);

        if (!hasAnyWeight) continue;

        const item = {
          product_group_id: Number(selectedProductGroup.value),
          tag_no: hasTag ? tagNoTrimmed : null,
          product_id: hasTag ? Number(productId) : null,
          gwt,
          nwt,
          fine_wt,
          purity: Number(rowData.purity) || 0,
          wastage: Number(rowData.wastage) || 0,
          item_rate_on: rowData.itRateOn ? String(rowData.itRateOn).trim() : "FWt",
          item_rate: Number(rowData.itRate) || 0,
        };

        settleItems.push(item);
      }

      const cashAccountId =
        bankAccounts.find(
          (b) => String(b.accountType || "").toLowerCase() === "cash"
        )?.id || null;

      const paymentsSourceRows = purchasePaymentRows.map((r) => {
        const isCash = r.pay_mode === "cash";
        const chosen =
          r.type !== "" && r.type != null ? Number(r.type) || 0 : 0;
        const bank_account_id = isCash
          ? chosen > 0
            ? chosen
            : Number(cashAccountId) || 0
          : chosen;

        return {
          bank_account_id,
          type: bank_account_id,
          amount: r.amount,
          paymentId: r.paymentId,
          pay_mode: r.pay_mode,
          remarks: r.remarks ?? "",
        };
      });

      for (let i = 0; i < paymentsSourceRows.length; i++) {
        const r = paymentsSourceRows[i];
        const amt = Number(r.amount) || 0;
        if (amt <= 0) continue;
        const bid = Number(r.bank_account_id) || 0;
        if (!Number.isFinite(bid) || bid <= 0) {
          toast.error(
            `Payment row ${i + 1}: select a Cash/Bank account when amount is greater than 0.`
          );
          return;
        }
      }

      const paymentsPayload = mapPurchasePaymentsForUpdateApi(
        paymentsSourceRows,
        {
          isUpdate: false,
          paymentTypes,
          createKey: createPurchasesIdempotencyKey,
        },
      ).filter((p) => (Number(p.amount) || 0) > 0);

      if (settleItems.length === 0 && paymentsPayload.length === 0) {
        toast.error("Add at least one metal line or a payment amount to settle.");
        return;
      }

      const branchIdRaw = userData?.branch_id ?? userData?.branchId;
      const branch_id =
        branchIdRaw != null && branchIdRaw !== ""
          ? Number(branchIdRaw)
          : NaN;

      const createPayloadForHash = {
        type: "settle",
        supplier_id: Number(partyId),
        date: formattedDate,
        items: settleItems,
        payments: paymentsPayload,
        ...(Number.isFinite(branch_id) && branch_id > 0
          ? { branch_id }
          : {}),
      };

      const fingerprint = JSON.stringify(createPayloadForHash);

      if (
        !purchaseCreateIdempotencyKeyRef.current ||
        purchaseCreatePayloadFingerprintRef.current !== fingerprint
      ) {
        purchaseCreateIdempotencyKeyRef.current = createPurchasesIdempotencyKey();
        purchaseCreatePayloadFingerprintRef.current = fingerprint;
      }

      const response = await settleRateCut({
        ...createPayloadForHash,
        idempotency_key: purchaseCreateIdempotencyKeyRef.current,
      });

      if (response?.success) {
        purchaseCreateIdempotencyKeyRef.current = null;
        purchaseCreatePayloadFingerprintRef.current = null;

        toast.success(response?.message || "Rate cut settled successfully");

        navigate("/purchasetable");
      } else {
        console.error("API Error:", response?.message || response);
        toast.error(response?.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      console.error("Error details:", error.response?.data || error.message);
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to submit the form. Please try again."
      );
    }
  };

  // Handle customer mobile input change
  const handleCustomerMobileChange = (e) => {
    const value = sanitizeMobileInput(e.target.value);
    setCustomerMobileSearchText(value);
    setCustomerMobile(value);
    setCustomerMobileDropdownActive(true);
    setCustomerMobileHighlightedIndex(0);
    setSelectedCustomerId(null);
    setIsNewCustomer(true);
  };

  // Get filtered users based on mobile number
  const getFilteredUserssByMobile = (searchText) => {
    if (!searchText || searchText.length < 3) return [];
    const searchLower = searchText.toLowerCase();
    return userss.filter((item) =>
      item.mobile.toLowerCase().includes(searchLower)
    );
  };

  // Function to handle customer mobile selection
  const handleCustomerMobileSelect = (selectedItem) => {
    setCustomerMobile(selectedItem.mobile);
    setCustomerMobileSearchText(selectedItem.mobile);
    setCustomerMobileDropdownActive(false);
    setSelectedCustomerId(selectedItem.id);
    setCustomerName(selectedItem.fullName);
    setCustomerNameSearchText(selectedItem.fullName);
    setCustomerAddress(selectedItem.address);
    setCustomerCity(selectedItem.city);
    setCustomerDetails({
      email: selectedItem.email || "",
      birthDate: selectedItem.birthDate || "",
      anniversaryDate: selectedItem.anniversaryDate || "",
      companyName: selectedItem.companyName || "",
      companyAddress: selectedItem.companyAddress || "",
      gstNumber: selectedItem.gstNumber || "",
      aadharNo: selectedItem.aadharNo || "",
      panNo: selectedItem.panNo || "",
      maritalStatus: selectedItem.maritalStatus || "",
      balance: selectedItem.balance || "",
      saleBalance: selectedItem.saleBalance || "",
      reference: selectedItem.reference || "",
      state: selectedItem.state || "",
    });
    setIsNewCustomer(false);
  };

  // Handle key navigation in customer mobile dropdown
  const handleCustomerMobileKeyDown = (e) => {
    const filtered = getFilteredUserssByMobile(customerMobileSearchText);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setCustomerMobileHighlightedIndex((prevIndex) => {
          const newIndex =
            prevIndex < filtered.length - 1 ? prevIndex + 1 : prevIndex;
          // Scroll the highlighted item into view
          if (customerMobileItemsRef.current[newIndex]) {
            customerMobileItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setCustomerMobileHighlightedIndex((prevIndex) => {
          const newIndex = prevIndex > 0 ? prevIndex - 1 : 0;
          // Scroll the highlighted item into view
          if (customerMobileItemsRef.current[newIndex]) {
            customerMobileItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (filtered.length > 0 && customerMobileDropdownActive) {
          handleCustomerMobileSelect(filtered[customerMobileHighlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setCustomerMobileDropdownActive(false);
        break;
      case "Tab":
        setCustomerMobileDropdownActive(false);
        break;
      default:
        break;
    }
  };

  // Close customer mobile dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        customerMobileDropdownRef.current &&
        !customerMobileDropdownRef.current.contains(event.target) &&
        customerMobileInputRef.current &&
        !customerMobileInputRef.current.contains(event.target)
      ) {
        setCustomerMobileDropdownActive(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Render customer mobile dropdown
  const renderCustomerMobileDropdown = () => {
    if (!customerMobileDropdownActive) return null;

    const filtered = getFilteredUserssByMobile(customerMobileSearchText);

    // Reset refs array
    customerMobileItemsRef.current = [];

    // Calculate width based on input field
    const inputWidth = customerMobileInputRef.current
      ? customerMobileInputRef.current.offsetWidth
      : 150;
    const inputRect = customerMobileInputRef.current
      ? customerMobileInputRef.current.getBoundingClientRect()
      : null;

    if (!inputRect) return null;

    return ReactDOM.createPortal(
      <div
        ref={customerMobileDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${inputRect.bottom}px`,
          left: `${inputRect.left}px`,
          width: `${inputWidth}px`,
          minWidth: `${inputWidth}px`,
          maxWidth: `${inputWidth}px`,
          maxHeight: "150px", // Reduced from 200px to show ~5 items
          overflowY: "auto",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
          fontSize: "12px",
          lineHeight: "1.2",
        }}
      >
        {filtered.length > 0 ? (
          filtered.map((item, idx) => {
            const isSelected =
              item.mobile.toLowerCase() ===
              customerMobileSearchText.toLowerCase();

            return (
              <div
                key={idx}
                ref={(el) => (customerMobileItemsRef.current[idx] = el)}
                className={`dropdown-item ${idx === customerMobileHighlightedIndex ? "active" : ""
                  }`}
                style={{
                  cursor: "pointer",
                  padding: "0.25rem 1rem",
                  fontSize: "0.85rem",
                  backgroundColor:
                    idx === customerMobileHighlightedIndex
                      ? "var(--theme-primary)"
                      : isSelected
                        ? "#f0f0f0"
                        : "transparent",
                  color:
                    idx === customerMobileHighlightedIndex ? "#fff" : "inherit",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  transition: "background-color 0.2s, color 0.2s", // Add transition for smooth highlight
                }}
                onMouseEnter={() => setCustomerMobileHighlightedIndex(idx)}
                onMouseDown={() => handleCustomerMobileSelect(item)}
              >
                {item.mobile} - {item.name}
              </div>
            );
          })
        ) : (
          <div className="dropdown-item disabled">No matching users</div>
        )}
      </div>,
      document.getElementById("dropdown-portal")
    );
  };

  // Handle customer name input change
  const handleCustomerNameChange = (e) => {
    const value = e.target.value;
    setCustomerNameSearchText(value);
    setCustomerNameDropdownActive(true);
    setCustomerNameHighlightedIndex(0);
    setSelectedCustomerId(null);
    setIsNewCustomer(true);
  };

  // Get filtered users based on name
  const getFilteredUserssByName = (searchText) => {
    if (!searchText || searchText.length < 3) return [];
    const searchLower = searchText.toLowerCase();
    return USERS.filter((item) =>
      item.name.toLowerCase().includes(searchLower)
    );
  };
  // Function to handle customer name selection
  const handleCustomerNameSelect = (selectedItem) => {
    setCustomerName(selectedItem.name);
    setCustomerNameSearchText(selectedItem.name);
    setCustomerNameDropdownActive(false);
    setSelectedCustomerId(selectedItem.id);

    // Auto-fill other customer fields
    setCustomerMobile(selectedItem.mobile);
    setCustomerMobileSearchText(selectedItem.mobile);
    setCustomerAddress(selectedItem.address);
    setCustomerCity(selectedItem.city);

    // Set all customer details
    setCustomerDetails({
      email: selectedItem.email || "",
      birthDate: selectedItem.birthDate || "",
      anniversaryDate: selectedItem.anniversaryDate || "",
      companyName: selectedItem.companyName || "",
      companyAddress: selectedItem.companyAddress || "",
      gstNumber: selectedItem.gstNumber || "",
      aadharNo: selectedItem.aadharNo || "",
      panNo: selectedItem.panNo || "",
      maritalStatus: selectedItem.maritalStatus || "",
      // openingBalance: selectedItem.openingBalance || "",
      // pendingBalance: selectedItem.pendingBalance || "",
      balance: selectedItem.balance || "",
      saleBalance: selectedItem.saleBalance || "",
      reference: selectedItem.reference || "",
      state: selectedItem.state || "",
    });

    setIsNewCustomer(false);
  };

  // Handle key navigation in customer name dropdown
  const handleCustomerNameKeyDown = (e) => {
    const filtered = getFilteredUserssByName(customerNameSearchText);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setCustomerNameHighlightedIndex((prevIndex) => {
          const newIndex =
            prevIndex < filtered.length - 1 ? prevIndex + 1 : prevIndex;
          // Scroll the highlighted item into view
          if (customerNameItemsRef.current[newIndex]) {
            customerNameItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setCustomerNameHighlightedIndex((prevIndex) => {
          const newIndex = prevIndex > 0 ? prevIndex - 1 : 0;
          // Scroll the highlighted item into view
          if (customerNameItemsRef.current[newIndex]) {
            customerNameItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (filtered.length > 0 && customerNameDropdownActive) {
          handleCustomerNameSelect(filtered[customerNameHighlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setCustomerNameDropdownActive(false);
        break;
      case "Tab":
        setCustomerNameDropdownActive(false);
        break;
      default:
        break;
    }
  };

  // Close customer name dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        customerNameDropdownRef.current &&
        !customerNameDropdownRef.current.contains(event.target) &&
        customerNameInputRef.current &&
        !customerNameInputRef.current.contains(event.target)
      ) {
        setCustomerNameDropdownActive(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Render customer name dropdown
  const renderCustomerNameDropdown = () => {
    if (!customerNameDropdownActive) return null;

    const filtered = getFilteredUserssByName(customerNameSearchText);

    // Reset refs array
    customerNameItemsRef.current = [];

    // Calculate width based on input field
    const inputWidth = customerNameInputRef.current
      ? customerNameInputRef.current.offsetWidth
      : 150;
    const inputRect = customerNameInputRef.current
      ? customerNameInputRef.current.getBoundingClientRect()
      : null;

    if (!inputRect) return null;

    return ReactDOM.createPortal(
      <div
        ref={customerNameDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${inputRect.bottom}px`,
          left: `${inputRect.left}px`,
          width: `${inputWidth}px`,
          minWidth: `${inputWidth}px`,
          maxWidth: `${inputWidth}px`,
          maxHeight: "150px", // Reduced from 200px to show ~5 items
          overflowY: "auto",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
          fontSize: "12px",
          lineHeight: "1.2",
        }}
      >
        {filtered.length > 0 ? (
          filtered.map((item, idx) => {
            const isSelected =
              item.name.toLowerCase() === customerNameSearchText.toLowerCase();

            return (
              <div
                key={idx}
                ref={(el) => (customerNameItemsRef.current[idx] = el)}
                className={`dropdown-item ${idx === customerNameHighlightedIndex ? "active" : ""
                  }`}
                style={{
                  cursor: "pointer",
                  padding: "0.25rem 1rem",
                  fontSize: "0.85rem",
                  backgroundColor:
                    idx === customerNameHighlightedIndex
                      ? "var(--theme-primary)"
                      : isSelected
                        ? "#f0f0f0"
                        : "transparent",
                  color:
                    idx === customerNameHighlightedIndex ? "#fff" : "inherit",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  transition: "background-color 0.2s, color 0.2s", // Add transition for smooth highlight
                }}
                onMouseEnter={() => setCustomerNameHighlightedIndex(idx)}
                onMouseDown={() => handleCustomerNameSelect(item)}
              >
                {item.name} - {item.mobile}
              </div>
            );
          })
        ) : (
          <div className="dropdown-item disabled">No matching users</div>
        )}
      </div>,
      document.getElementById("dropdown-portal")
    );
  };

  // Handle customer address change
  const handleCustomerAddressChange = (e) => {
    setCustomerAddress(e.target.value);
  };

  // Handle customer city change
  const handleCustomerCityChange = (e) => {
    setCustomerCity(e.target.value);
  };

  // Function to save a new customer
  const handleSaveCustomer = async () => {
    if (!customerMobileSearchText || !customerNameSearchText) {
      toast.error("Mobile number and name are required");
      return;
    }

    try {
      const response = await createCustomerUser({
        mobile: customerMobileSearchText.trim(),
        name: customerNameSearchText.trim(),
        address: customerAddress || undefined,
        city: customerCity || undefined,
        state: customerDetails.state || undefined,
      });

      if (isErpApiSuccess(response)) {
        toast.success("Customer created successfully");

        // Reset fields after success
        setCustomerMobile("");
        setCustomerMobileSearchText("");
        setCustomerName("");
        setCustomerNameSearchText("");
        setCustomerAddress("");
        setCustomerCity("");
        setCustomerDetails((prev) => ({ ...prev, state: "" }));

        setIsNewCustomer(false);
        customerSearchCacheRef.current = new Set();
      } else {
        toast.error(response?.message || "Failed to save customer");
      }
    } catch (error) {
      console.error("Error saving customer:", error);
      toast.error("Error saving customer");
    }
  };

  // Follow-up date state (kept as it's used for purchase follow-up, not just payments)
  const [followUpDate, setFollowUpDate] = useState("");

  // Add CSS to remove number input spinners
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      /* Remove number input spinners */
      input[type="number"]::-webkit-inner-spin-button,
      input[type="number"]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      input[type="number"] {
        -moz-appearance: textfield;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Handle customer state change
  const handleCustomerStateChange = (e) => {
    setCustomerDetails({
      ...customerDetails,
      state: e.target.value,
    });
  };

  // Handle round-off amount change (any edit locks manual mode; empty = no round off)
  const handleRoundOffChange = (e) => {
    handleRoundOffInputChange(
      e,
      (value) => {
        setIsManualRoundOff(true);
        setRoundOffAmount(value);
      },
      {
        allowNegative: true,
        simple: true,
      },
    );
  };

  // Handle note change
  const handleNoteChange = (e) => {
    const value = e.target.value;
    if (value.length <= 70) {
      setNote(value);
    }
  };

  const handleDeleteRow = (rowIndex) => {
    setTableData((prevData) => {
      const newData = prevData.filter((_, index) => index !== rowIndex);
      return newData;
    });
  };

  const location = useLocation();
  const customOrderId = location.state?.customOrderId;
  const customerUserId = location.state?.customerUserId;
  const repairingOrderId = location.state?.repairingOrderId;
  const isFromRepairingOrder = location.state?.isFromRepairingOrder;
  const [hasFilledFromCustomOrder, setHasFilledFromCustomOrder] =
    useState(false);
  const [customOrderData, setCustomOrderData] = useState(null);
  const [isLoadingOrderData, setIsLoadingOrderData] = useState(false);

  // Fetch order data using show_order_queue API
  const fetchOrderData = async (orderId) => {
    if (!orderId) return;

    setIsLoadingOrderData(true);
    try {
      const response = await showOrderQueue({});
      const d = response?.data;
      let orders = [];
      if (isErpApiSuccess(response)) {
        if (Array.isArray(d)) orders = d;
        else if (Array.isArray(d?.data)) orders = d.data;
        else if (Array.isArray(d?.orders)) orders = d.orders;
        else if (Array.isArray(d?.records)) orders = d.records;
      }

      const foundOrder = orders.find(
        (order) => String(order.id) === String(orderId)
      );
      if (foundOrder) {
        setCustomOrderData(foundOrder);

        // Set customer name and mobile from the order's user_id
        if (foundOrder.user_id && userss && userss.length > 0) {
          const customer = userss.find(
            (user) => String(user.id) === String(foundOrder.user_id)
          );
          if (customer) {
            setCustomerName(customer.fullName || customer.name || "");
            setCustomerNameSearchText(customer.fullName || customer.name || "");
            setCustomerMobile(customer.mobile || "");
            setCustomerMobileSearchText(customer.mobile || "");
            setSelectedCustomerId(customer.id);
            setCustomerCity(customer.city || "");
            setCustomerAddress(customer.address || "");
            setCustomerDetails((prevDetails) => ({
              ...prevDetails,
              email: customer.email || "",
              birthDate: customer.birthDate || "",
              anniversaryDate: customer.anniversaryDate || "",
              companyName: customer.companyName || "",
              companyAddress: customer.companyAddress || "",
              gstNumber: customer.gstNumber || "",
              aadharNo: customer.aadharNo || "",
              panNo: customer.panNo || "",
              maritalStatus: customer.maritalStatus || "",
              balance: customer.balance || "",
              saleBalance: customer.saleBalance || "",
              reference: customer.reference || "",
              state: customer.state || "",
            }));
          }
        }

        // Fetch products from order queue
        await fetchProductsFromOrderQueue(foundOrder.products || []);
      } else {
        console.error("Order not found with ID:", orderId);
        toast.error("Order not found");
      }
    } catch (error) {
      console.error("Error fetching order data:", error);
      toast.error("Failed to fetch order data");
    } finally {
      setIsLoadingOrderData(false);
    }
  };

  // Fetch product details for order products using show_pro API with type "all"
  const fetchProductsFromOrderQueue = async (orderProducts) => {
    try {
      // console.log(
      //   "Order Products:",
      //   orderProducts.map((product) => product.pro_id).join(",")
      // );

      const ids = orderProducts.map((p) => p.pro_id).filter((x) => x != null && x !== "");
      if (!ids.length) {
        toast.error("No product IDs in order");
        return;
      }
      const response = await showProducts({ id: ids });
      const allProducts = normalizeProductStockShowResponse(response);
      if (isErpApiSuccess(response) && response.data) {
        if (allProducts.length === 0) {
          console.warn("No products found in response");
          toast.error("No product details found");
          return;
        }
        setAllProducts(allProducts);

        // Process each product in the order
        const processedProducts = orderProducts
          .map((orderProduct, index) => {
            const product = allProducts.find(
              (p) => String(p.id) === String(orderProduct.pro_id)
            );
            if (product) {
              // console.log(
              //   `Found product for pro_id ${orderProduct.pro_id}:`,
              //   product
              // );
              return {
                id: index + 1,
                data: COLUMNS.map((col) => {
                  let value = "";
                  switch (col.id) {
                    case "tagNo":
                      value = product.tag_no || product.tagNo || "";
                      break;
                    case "productGroup": {
                      const rawId =
                        product.product_group ??
                        product.product_group_id ??
                        product.productGroupId ??
                        null;
                      const group = dropdownData.productGroup?.find(
                        (pg) => String(pg.value) === String(rawId)
                      );
                      value =
                        group?.label ||
                        product.product_group_name ||
                        product.product_group ||
                        "";
                      break;
                    }
                    case "pcs":
                      value = product.pcs || "";
                      break;
                    case "purity":
                      value = product.purity || "";
                      break;
                    case "wastage":
                      value = product.wastage || "";
                      break;
                    case "grWt":
                      value = product.GWT || "";
                      break;
                    case "lsWt":
                      value = product.LWT || "";
                      break;
                    case "netWt":
                      value = product.NWT || "";
                      break;
                    case "itRateOn":
                      value = "FWt"; // Always use fineWt
                      break;
                    case "fineWt":
                      // Calculate fineWt from netWt, purity, and wastage
                      const netWt = parseFloat(product.NWT) || 0;
                      const purity = parseFloat(product.purity) || 0;
                      const wastage = parseFloat(product.wastage) || 0;
                      value = calculateFineWeight(
                        netWt,
                        purity,
                        wastage
                      ).toFixed(2);
                      break;
                    case "itRate":
                      value = product.itrate || "";
                      break;
                    case "itAmt":
                      value = product.itamt || "";
                      break;
                    case "othAmt":
                      value = product.othamt || "";
                      break;
                    case "totalAmt":
                      value = product.totalamt || "";
                      break;
                    default:
                      value = "";
                  }
                  return { id: col.id, value };
                }),
              };
            } else {
              // console.log(
              //   `Product not found for pro_id: ${orderProduct.pro_id}`
              // );
              return null;
            }
          })
          .filter(Boolean); // Remove null entries

        if (processedProducts.length > 0) {
          setTableData(processedProducts);
        }
      } else {
        console.error("Unexpected product data format:", response.data);
      }
    } catch (error) {
      console.error("Error fetching product details for order:", error);
      toast.error("Failed to fetch product details");
    }
  };

  // Fetch order data when only customOrderId is provided
  useEffect(() => {
    if (
      customOrderId &&
      !location.state?.customOrderData &&
      userss &&
      userss.length > 0 &&
      dropdownData.productGroup &&
      dropdownData.productGroup.length > 0
    ) {
      fetchOrderData(customOrderId);
    }
  }, [customOrderId, userss, dropdownData.productGroup]);

  // Recalculate It Rate from live price when master rates change
  useEffect(() => {
    if (!tableData || tableData.length === 0) return;
    setTableData((prev) => {
      let updated = [...prev];
      for (let i = 0; i < updated.length; i++) {
        const pgCell = updated[i].data.find((c) => c.id === "productGroup");
        if (!pgCell || !pgCell.value) continue;
        updated = calculateAndUpdateItRate(updated, i);
      }
      return updated;
    });
  }, [masterGoldRate, masterSilverRate]);

  // Replace the old useEffect with the custom hook
  useFillSalesFromCustomOrder({
    locationState: customOrderData
      ? {
        customOrderId: customOrderId,
        customOrderTable: customOrderData.products || [],
        customOrderData: customOrderData,
        customOrderPayments: customOrderData.payments || [],
      }
      : location.state,
    setTableData,
    setPaymentRows: () => { }, // No-op function since payment table is removed
    setRoundOffAmount: (value) => {
      setRoundOffAmount(value);
      setIsManualRoundOff(true);
    },
    setNote,
    COLUMNS,
    dropdownData,
    hasFilledFromCustomOrder,
    setHasFilledFromCustomOrder,
    userss,
    setCustomerName,
    setCustomerNameSearchText,
    setCustomerMobile,
    setCustomerMobileSearchText,
    setSelectedCustomerId,
    setCustomerDetails,
    setCustomerCity,
    setCustomerAddress,
    setSelectedState,
    states,
    cities,
    allProducts,
    productGroups: dropdownData.productGroup || [],
  });

  // Pre-fill customer data when customerUserId is provided
  useEffect(() => {
    if (customerUserId && userss && userss.length > 0) {
      const customerUser = userss.find(
        (user) => String(user.id) === String(customerUserId)
      );
      if (customerUser) {
        setCustomerName(customerUser.name || customerUser.fullName || "");
        setCustomerNameSearchText(
          customerUser.name || customerUser.fullName || ""
        );
        setCustomerMobile(customerUser.mobile || "");
        setCustomerMobileSearchText(customerUser.mobile || "");
        setSelectedCustomerId(customerUser.id);
        setCustomerCity(customerUser.city || "");
        setCustomerAddress(customerUser.address || "");
        // Set additional customer details
        setCustomerDetails((prevDetails) => ({
          ...prevDetails,
          email: customerUser.email || "",
          birthDate: customerUser.birthDate || "",
          anniversaryDate: customerUser.anniversaryDate || "",
          companyName: customerUser.companyName || "",
          companyAddress: customerUser.companyAddress || "",
          gstNumber: customerUser.gstNumber || "",
          aadharNo: customerUser.aadharNo || "",
          panNo: customerUser.panNo || "",
          maritalStatus: customerUser.maritalStatus || "",
          balance: customerUser.balance || "",
          saleBalance: customerUser.saleBalance || "",
          reference: customerUser.reference || "",
          state: customerUser.state || "",
        }));
        // Set state if available
        if (customerUser.state) {
          setSelectedState(customerUser.state);
        }
      }
    }
  }, [customerUserId, userss]);

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div className={`card shadow-sm ${cardClass}`}>
          <div className="px-4 py-3 border-bottom">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <h4 className="card-title mb-0">{pageTitle}</h4>
              {partyId && (
                <div className="d-flex align-items-center flex-wrap gap-2 ms-auto">
                  <span
                    className="badge bg-primary-subtle text-primary"
                    style={{ fontSize: "0.85rem" }}
                  >
                    {isSupplierBalanceLoading ? (
                      <>Supplier: Loading...</>
                    ) : (
                      (() => {
                        const money = formatSettlementBadge(
                          supplierBalances.money,
                          "₹",
                          "",
                          supplierBalances.money_status,
                        );
                        const gold = formatSettlementBadge(
                          supplierBalances.gold_gm,
                          "",
                          " gm",
                          supplierBalances.gold_status,
                        );
                        const silver = formatSettlementBadge(
                          supplierBalances.silver_gm,
                          "",
                          " gm",
                          supplierBalances.silver_status,
                        );

                        return (
                          <>
                            Money: {money.label}
                            {money.valueText ? ` (${money.valueText})` : ""}
                            {"  "}•{"  "}
                            Gold: {gold.label}
                            {gold.valueText ? ` (${gold.valueText})` : ""}
                            {"  "}•{"  "}
                            Silver: {silver.label}
                            {silver.valueText ? ` (${silver.valueText})` : ""}
                          </>
                        );
                      })()
                    )}
                  </span>
                </div>
              )}
              {selectedCustomerId && (
                <div className="d-flex align-items-center flex-wrap gap-2 ms-auto">
                  <span
                    className={`badge ${parseFloat(customerDetails.balance) >= 0
                      ? "bg-success-subtle text-success"
                      : "bg-danger-subtle text-danger"
                      }`}
                    style={{ fontSize: "0.85rem" }}
                  >
                    Balance: ₹
                    {parseFloat(customerDetails.balance || 0).toFixed(2)}
                  </span>
                  <span
                    className={`badge ${parseFloat(customerDetails.saleBalance) > 0
                      ? "bg-danger-subtle text-danger"
                      : "bg-success-subtle text-success"
                      } ms-2`}
                    style={{ fontSize: "0.85rem" }}
                  >
                    Pending: ₹
                    {parseFloat(customerDetails.saleBalance || 0).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="card-body">
            <div className="row mb-1">
              {/* Date Component */}
              <div className="col-lg-3 col-md-4 col-sm-6 mb-2">
                <div className="d-flex align-items-center">
                  <label
                    className="form-label me-2 mb-0"
                    style={{
                      width: "50px",
                      fontSize: "0.85rem",
                      fontWeight: "600",
                    }}
                  >
                    Date:
                  </label>
                  <input
                    type="date"
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={billDate}
                    onChange={handleDateChange}
                    style={{ fontSize: "0.85rem" }}
                  />
                </div>
              </div>

              {/* Party Name (search, 2+ chars) - searches karigar directly */}
              <div className="col-lg-3 col-md-5 col-sm-6 mb-2">
                <div className="d-flex align-items-center position-relative">
                  <label
                    className="form-label me-1 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "55px" }}
                  >
                    Name: 
                  </label>
                  <div className="position-relative flex-grow-1">
                    <input
                      ref={partyNameInputRef}
                      type="text"
                      className={`form-control form-control-sm ${buttonClass}`}
                      placeholder="Name"
                      value={partyName}
                      onChange={handlePartyNameChange}
                      onFocus={handlePartyNameFocus}
                      onBlur={handlePartyNameBlur}
                      onKeyDown={handlePartyNameKeyDown}
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>

              {/* Party Mobile (read only) */}
              <div className="col-lg-3 col-md-7 col-sm-12 mb-2">
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="text"
                    className={`form-control form-control-sm ${buttonClass}`}
                    placeholder="Mobile No"
                    value={partyMobile}
                    readOnly
                  />
                </div>
              </div>
            </div>

            <div
              className={`table-responsive border ${buttonTableStyle === "square" ? "rounded-0" : "rounded-2"
                } mt-3 ${cardClass}`}
            >
              <table
                className={`table table-bordered table-hover table-sm mb-0 theme-header-table ${tableClass}`}
              >
                <thead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.id}
                        className="px-2 py-2 text-center"
                        style={{
                          fontSize: "0.85rem",
                          width: column.width,
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, rowIndex) => (
                    <tr key={row.id} style={{ backgroundColor: "#ffffff" }}>
                      {row.data.map((cell, colIndex) => {
                        // Add special handling for serial number column
                        if (cell.id === "no") {
                          return (
                            <td
                              key={cell.id}
                              className="p-0"
                              style={{ width: COLUMNS[colIndex]?.width }}
                            >
                              <div
                                className="d-flex align-items-center justify-content-center"
                                style={{ height: "38px" }}
                              >
                                <span
                                  style={{
                                    fontSize: "0.85rem",
                                    cursor: "pointer",
                                    transition: "opacity 0.2s",
                                  }}
                                  onClick={() => handleDeleteRow(rowIndex)}
                                  onMouseEnter={(e) => {
                                    e.target.textContent = "×";
                                    e.target.style.fontWeight = "600";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.textContent = rowIndex + 1;
                                    e.target.style.fontWeight = "normal";
                                  }}
                                >
                                  {rowIndex + 1}
                                </span>
                              </div>
                            </td>
                          );
                        }

                        const hasOtherAmountEntries =
                          row.data.find((c) => c.id === "othAmt")
                            ?.otherAmountEntries?.length > 0;
                        const hasFieldError = !!itemFieldErrors[row.id]?.includes(
                          cell.id,
                        );

                        return (
                          <td
                            key={cell.id}
                            className="p-0"
                            style={{
                              width: COLUMNS[colIndex]?.width,
                              position: "relative",
                              ...lineCellErrorStyle(hasFieldError),
                            }}
                          >
                            {isRowDropdownColumn(getColumnType(colIndex), row) ? (
                              // Dropdown-enabled cell
                              <div className="position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      newValue
                                    );
                                    // Show dropdown when typing; tagNo/productGroup need 3+ characters
                                    const columnType = getColumnType(colIndex);

                                    const requiresMinLength =
                                      columnType === "tagNo" ||
                                      columnType === "productGroup";
                                    const meetsMinLength =
                                      !requiresMinLength ||
                                      newValue.length >= 3;

                                    // Only show dropdown if minimum length is met (or not required)
                                    if (meetsMinLength) {
                                      setTimeout(() => {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType
                                        );
                                      }, 0);
                                    } else {
                                      // Close dropdown if less than 3 characters
                                      setActiveDropdown({
                                        row: null,
                                        col: null,
                                        type: null,
                                      });
                                    }
                                  }}
                                  onKeyDown={(e) =>
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex)
                                    )
                                  }
                                  onClick={() => {
                                    const columnType = getColumnType(colIndex);
                                    if (columnType === "tagNo" || columnType === "productGroup") {
                                      if (
                                        cell.value &&
                                        cell.value.length >= 3
                                      ) {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType
                                        );
                                      }
                                    } else {
                                      handleDropdownClick(
                                        rowIndex,
                                        colIndex,
                                        columnType
                                      );
                                    }
                                  }}
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  autoComplete="off"
                                />
                              </div>
                            ) : isTagLockedField(getColumnType(colIndex), row) ? (
                              <input
                                ref={(el) =>
                                (inputRefs.current[
                                  `${rowIndex}-${colIndex}`
                                ] = el)
                                }
                                type="text"
                                value={cell.value}
                                readOnly
                                className="form-control border-0 rounded-0"
                                style={{
                                  fontSize: "0.85rem",
                                  padding: "0.4rem 0.5rem",
                                  backgroundColor: "#f0f0f0",
                                  width: "100%",
                                  cursor: "default",
                                }}
                                tabIndex={-1}
                              />
                            ) : getColumnType(colIndex) === "lsWt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    if (!hasOtherAmountEntries) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value
                                        );
                                      }
                                    }
                                  }}
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: hasOtherAmountEntries
                                      ? "not-allowed"
                                      : "text",
                                  }}
                                  onFocus={(e) => {
                                    if (!hasOtherAmountEntries) {
                                      handleFocus(rowIndex, colIndex);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  readOnly={hasOtherAmountEntries}
                                  title={
                                    hasOtherAmountEntries
                                      ? "This value is calculated from Other Amount entries"
                                      : ""
                                  }
                                />
                              </div>
                            ) : getColumnType(colIndex) === "othAmt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no entries
                                    if (!cell.otherAmountEntries?.length) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value
                                        );
                                      }
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex)
                                    );
                                    handleOtherAmountInteraction(rowIndex, e);
                                  }}
                                  onDoubleClick={(e) =>
                                    handleOtherAmountInteraction(rowIndex, e)
                                  }
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: "pointer",
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) =>
                                  (e.target.style.backgroundColor =
                                    "transparent")
                                  }
                                  title={
                                    cell.otherAmountEntries?.length > 0
                                      ? "Double-click or press Enter twice to edit details"
                                      : "Enter amount directly or double-click/press Enter twice for details"
                                  }
                                />

                                {cell.otherAmountEntries?.length > 0 && (
                                  <div className="position-relative">
                                    <Button
                                      variant="link"
                                      className="p-0 px-1 text-primary"
                                      style={{
                                        fontSize: "1rem",
                                        position: "absolute",
                                        right: "5px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 1,
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // If tooltip is already visible, hide it
                                          if (
                                            tooltip.style.display === "block"
                                          ) {
                                            tooltip.style.display = "none";
                                            setActiveTooltip(null);

                                            // Clear any existing timeout
                                            if (tooltipTimeoutRef.current) {
                                              clearTimeout(
                                                tooltipTimeoutRef.current
                                              );
                                              tooltipTimeoutRef.current = null;
                                            }
                                            return;
                                          }

                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);

                                          // Set timeout to hide after 4 seconds
                                          if (tooltipTimeoutRef.current) {
                                            clearTimeout(
                                              tooltipTimeoutRef.current
                                            );
                                          }

                                          tooltipTimeoutRef.current =
                                            setTimeout(() => {
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                                setActiveTooltip(null);
                                                tooltipTimeoutRef.current =
                                                  null;
                                              }
                                            }, 4000);
                                        }
                                      }}
                                      onMouseEnter={(e) => {
                                        // Don't show on hover if already showing from a click
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={() => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (
                                          tooltip &&
                                          activeTooltip === tooltipId
                                        ) {
                                          // Use setTimeout to allow moving to the tooltip
                                          setTimeout(() => {
                                            // Only hide if we're not hovering over the tooltip itself
                                            if (
                                              activeTooltip === tooltipId &&
                                              !tooltip.matches(":hover") &&
                                              !tooltipTimeoutRef.current
                                            ) {
                                              tooltip.style.display = "none";
                                              setActiveTooltip(null);
                                            }
                                          }, 100);
                                        }
                                      }}
                                    >
                                      <Icon icon="solar:info-circle-bold" />
                                    </Button>

                                    {/* Other Amount Details Tooltip */}
                                    <div
                                      ref={(el) =>
                                      (tooltipRefs.current[
                                        `oth-amt-tooltip-${rowIndex}`
                                      ] = el)
                                      }
                                      className="shadow-lg rounded p-2 bg-white tooltip-container"
                                      style={{
                                        display: "none",
                                        width: "auto",
                                        maxWidth: "250px",
                                        zIndex: 9999,
                                        position: "fixed",
                                      }}
                                      onMouseEnter={() => {
                                        // Do nothing when hovering tooltip
                                        // This prevents the tooltip from disappearing
                                      }}
                                      onMouseLeave={(e) => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        if (activeTooltip === tooltipId) {
                                          e.currentTarget.style.display =
                                            "none";
                                          setActiveTooltip(null);
                                        }
                                      }}
                                    >
                                      <div
                                        className="tooltip-arrow"
                                        style={{
                                          position: "absolute",
                                          width: "12px",
                                          height: "12px",
                                          backgroundColor: "white",
                                          border: "1px solid rgba(0,0,0,0.1)",
                                          transform: "rotate(45deg)",
                                          borderRight: "none",
                                          borderBottom: "none",
                                          boxShadow:
                                            "-2px -2px 2px rgba(0,0,0,0.1)",
                                        }}
                                      ></div>
                                      <div
                                        className="other-amount-details"
                                        style={{
                                          maxHeight: "200px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        {cell.otherAmountEntries.map(
                                          (entry, idx) => (
                                            <div
                                              key={idx}
                                              className="d-flex justify-content-between py-1"
                                              style={{
                                                borderBottom:
                                                  idx !==
                                                    cell.otherAmountEntries
                                                      .length -
                                                    1
                                                    ? "1px solid #eee"
                                                    : "none",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  flex: "1",
                                                  marginRight: "20px",
                                                }}
                                              >
                                                {entry.chargeType}
                                              </span>
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  minWidth: "80px",
                                                  textAlign: "right",
                                                }}
                                              >
                                                ₹{" "}
                                                {Number(entry.amount).toFixed(
                                                  2
                                                )}
                                              </span>
                                            </div>
                                          )
                                        )}
                                        <div className="mt-2 pt-2 border-top">
                                          <div className="d-flex justify-content-between">
                                            <strong className="text-dark">
                                              Total
                                            </strong>
                                            <strong className="text-dark">
                                              ₹{" "}
                                              {cell.otherAmountEntries
                                                .reduce(
                                                  (sum, entry) =>
                                                    sum + Number(entry.amount),
                                                  0
                                                )
                                                .toFixed(2)}
                                            </strong>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                            ) : getColumnType(colIndex) === "totalAmt" ? (
                              <input
                                ref={(el) =>
                                (inputRefs.current[
                                  `${rowIndex}-${colIndex}`
                                ] = el)
                                }
                                type="text"
                                value={cell.value ?? ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (
                                    value === "" ||
                                    /^\d*\.?\d*$/.test(value)
                                  ) {
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      value
                                    );
                                  }
                                }}
                                onKeyDown={(e) => {
                                  handleKeyDown(
                                    e,
                                    rowIndex,
                                    colIndex,
                                    getColumnType(colIndex)
                                  );
                                  handleTotalAmountInteraction(rowIndex, e);
                                }}
                                onDoubleClick={(e) =>
                                  handleTotalAmountInteraction(rowIndex, e)
                                }
                                className="form-control border-0 rounded-0"
                                style={{
                                  fontSize: "0.85rem",
                                  padding: "0.4rem 0.5rem",
                                  backgroundColor: "transparent",
                                  width: "100%",
                                  transition: "background-color 0.2s",
                                }}
                                onFocus={() => handleFocus(rowIndex, colIndex)}
                                onBlur={(e) => {
                                  e.target.style.backgroundColor =
                                    "transparent";
                                }}
                                title={`Double-click or press Enter twice to enter amount details${cell?.totalAmountDetails?.gst
                                  ? ` (GST ${cell.totalAmountDetails.gst}%)`
                                  : ""
                                  }`}
                              />
                            ) : (
                              <input
                                ref={(el) =>
                                (inputRefs.current[
                                  `${rowIndex}-${colIndex}`
                                ] = el)
                                }
                                type="text"
                                value={cell.value}
                                readOnly={
                                  getColumnType(colIndex) === "fineWt" ||
                                  getColumnType(colIndex) === "itRateOn"
                                }
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  handleInputChange(
                                    rowIndex,
                                    colIndex,
                                    newValue
                                  );
                                  const columnType = getColumnType(colIndex);
                                  if (isDropdownColumn(columnType)) {
                                    const requiresMinLength =
                                      columnType === "tagNo";
                                    const meetsMinLength =
                                      !requiresMinLength ||
                                      newValue.length >= 3;
                                    if (meetsMinLength) {
                                      setTimeout(() => {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType
                                        );
                                      }, 0);
                                    } else {
                                      setActiveDropdown({
                                        row: null,
                                        col: null,
                                        type: null,
                                      });
                                    }
                                  } else {
                                    setActiveDropdown({
                                      row: null,
                                      col: null,
                                      type: null,
                                    });
                                  }
                                }}
                                // disabled={COLUMNS[colIndex].id === "grWt"}
                                className="form-control border-0 rounded-0"
                                style={{
                                  fontSize: "0.85rem",
                                  padding: "0.4rem 0.5rem",
                                  backgroundColor:
                                    getColumnType(colIndex) === "fineWt" ||
                                      getColumnType(colIndex) === "itRateOn"
                                      ? "#f0f0f0"
                                      : "transparent",
                                  width: "100%",
                                  transition: "background-color 0.2s",
                                  cursor:
                                    getColumnType(colIndex) === "fineWt" ||
                                      getColumnType(colIndex) === "itRateOn"
                                      ? "default"
                                      : "text",
                                }}
                                onFocus={() => handleFocus(rowIndex, colIndex)}
                                onBlur={(e) => {
                                  e.target.style.backgroundColor =
                                    "transparent";
                                }}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Add Totals Row */}
                  <tr style={{ backgroundColor: "#f8f9fa" }}>
                    {/* No. */}
                    <td className="p-0" style={{ width: COLUMNS[0]?.width }}>
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          Total
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* Tag No */}
                    <td className="p-0"></td> {/* Product Group */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Pcs */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData.reduce((sum, row) => {
                            const pcs = parseFloat(
                              row.data.find((cell) => cell.id === "pcs")
                                ?.value || 0
                            );
                            return sum + (isNaN(pcs) ? 0 : pcs);
                          }, 0)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Gr.Wt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const gwt = parseFloat(
                                row.data.find((cell) => cell.id === "grWt")
                                  ?.value || 0
                              );
                              return sum + (isNaN(gwt) ? 0 : gwt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Ls.Wt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const lwt = parseFloat(
                                row.data.find((cell) => cell.id === "lsWt")
                                  ?.value || 0
                              );
                              return sum + (isNaN(lwt) ? 0 : lwt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Net.Wt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const nwt = parseFloat(
                                row.data.find((cell) => cell.id === "netWt")
                                  ?.value || 0
                              );
                              return sum + (isNaN(nwt) ? 0 : nwt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Purity (average) */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData.length > 0
                            ? (
                              tableData.reduce((sum, row) => {
                                const purity = parseFloat(
                                  row.data.find(
                                    (cell) => cell.id === "purity"
                                  )?.value || 0
                                );
                                return sum + (isNaN(purity) ? 0 : purity);
                              }, 0) / tableData.length
                            ).toFixed(2)
                            : "0.00"}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Wastage (average) */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData.length > 0
                            ? (
                              tableData.reduce((sum, row) => {
                                const wastage = parseFloat(
                                  row.data.find(
                                    (cell) => cell.id === "wastage"
                                  )?.value || 0
                                );
                                return sum + (isNaN(wastage) ? 0 : wastage);
                              }, 0) / tableData.length
                            ).toFixed(2)
                            : "0.00"}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Fine.Wt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const fineWt = parseFloat(
                                row.data.find((cell) => cell.id === "fineWt")
                                  ?.value || 0
                              );
                              return sum + (isNaN(fineWt) ? 0 : fineWt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* RateOn */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* ItRate */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {/* Average item rate - not sum */}
                          {tableData.length > 0
                            ? (
                              tableData.reduce((sum, row) => {
                                const itRate = parseFloat(
                                  row.data.find(
                                    (cell) => cell.id === "itRate"
                                  )?.value || 0
                                );
                                return sum + (isNaN(itRate) ? 0 : itRate);
                              }, 0) / tableData.length
                            ).toFixed(2)
                            : "0.00"}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* ItAmt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const itAmt = parseFloat(
                                row.data.find((cell) => cell.id === "itAmt")
                                  ?.value || 0
                              );
                              return sum + (isNaN(itAmt) ? 0 : itAmt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* OthAmt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const othAmt = parseFloat(
                                row.data.find((cell) => cell.id === "othAmt")
                                  ?.value || 0,
                              );
                              return sum + (isNaN(othAmt) ? 0 : othAmt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* TotalAmt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {calculateTotal().toFixed(2)}
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="text-end mt-3">
              {/* <button className="btn btn-primary" onClick={handleSubmit}>
                Add
              </button> */}
            </div>


            <PaymentSection
              paymentRows={purchasePaymentRows}
              onPaymentRowsChange={setPurchasePaymentRows}
              paymentTypes={paymentTypes}
              bankAccounts={bankAccounts}
              excludedPayModes={["old_gold", "old_silver"]}
              payableRows={payableRows}
              onPayableRowsChange={setPayableRows}
              note={note}
              onNoteChange={setNote}
              finalAmount={calculateTotal().toFixed(2)}
              discountType={discountType}
              onDiscountTypeChange={setDiscountType}
              discountValue={discountValue}
              onDiscountValueChange={setDiscountValue}
              discountAmount={calculateDiscountAmount().toFixed(2)}
              gstPercentage={gstPercentage}
              onGstPercentageChange={setGstPercentage}
              gstAmount={calculateGstAmount().total.toFixed(2)}
              roundOffAmount={getDisplayRoundOff()}
              onRoundOffChange={handleRoundOffChange}
              grandTotal={calculateGrandTotal()}
              totalPaid={calculatePurchaseTotalPaid()}
              payableAmount={calculatePurchasePayableAmount()}
              remainingAmount={calculatePurchaseRemaining()}
              followUpDate={followUpDate}
              onFollowUpDateChange={setFollowUpDate}
              buttonClass={buttonClass}
              cardClass={cardClass}
              buttonTableStyle={buttonTableStyle}
            />

            {/* Submit button at the bottom */}
            <div className="row mt-4">
              <div className="col-12">
                <div className="d-flex flex-wrap align-items-center justify-content-between">
                  <div className="d-flex flex-wrap align-items-center">
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="printCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="printCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="M17 17h2a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2m10-8V5a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v4" />
                            <path d="M7 15a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2z" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">Print</span>
                      </label>
                    </div>
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="pdfCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="pdfCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                            <path d="M5 12V5a2 2 0 0 1 2-2h7l5 5v4M5 18h1.5a1.5 1.5 0 0 0 0-3H5v6m12-3h2m1-3h-3v6m-6-6v6h1a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2z" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">PDF</span>
                      </label>
                    </div>
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="whatsappCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="whatsappCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="m3 21l1.65-3.8a9 9 0 1 1 3.4 2.9z" />
                            <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0za5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">
                          WhatsApp
                        </span>
                      </label>
                    </div>
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="emailCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="emailCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                            <path d="m3 7l9 6l9-6" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">Email</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <button
                      className={`btn btn-primary ${buttonClass}`}
                      onClick={handleSubmit}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {renderDropdown()}
      {renderTotalAmountModal()}
      {renderOtherAmountModal()}
      {renderPartyDropdown()}

      {/* Post-save navigation modal */}
      <Modal
        show={showPostSaveModal}
        onHide={() => handlePostSaveChoice(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Settlement Posted</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Settlement posted successfully.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            className={buttonClass}
            onClick={() => handlePostSaveChoice(false)}
          >
            Purchase Table
          </Button>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={() => handlePostSaveChoice(true)}
          >
            Stock Table
          </Button>
        </Modal.Footer>
      </Modal>

      {/* <p>Master Gold Rate: {masterGoldRate}</p>
      <p>Master Silver Rate: {masterSilverRate}</p> */}
      <Toaster position="top-center" />
    </div>
  );
};

export default PurchaseRateCut;
